// Import Supabase client
import { supabase } from './supabaseClient';

/**
 * Fetch gallery infrastructure elements
 * @param {string} galleryId - The gallery ID to fetch infrastructure for
 * @returns {Promise<Array>} - The gallery infrastructure data
 */
export const fetchGalleryInfrastructure = async (galleryId) => {
  try {
    console.log(`🏗️ Fetching gallery infrastructure for gallery ${galleryId}`);
    
    const { data, error } = await supabase
      .from('gallery_infrastructure')
      .select('*')
      .eq('gallery_id', galleryId)
      .eq('visible', true)
      .order('type', { ascending: true })
      .order('infrastructure_id', { ascending: true });
      
    if (error) throw error;
    
    console.log(`✅ Gallery infrastructure loaded: ${data?.length || 0} elements`);
    return data || [];
    
  } catch (error) {
    console.error('💥 Error fetching gallery infrastructure:', error);
    throw new Error(`Failed to fetch gallery infrastructure: ${error.message}`);
  }
};



/**
 * Fetch scene elements directly from Supabase (bypassing API)
 * @param {string} collectionId - The collection ID to fetch scene elements for
 * @param {string} viewMode - The view mode (3d or 2d)
 * @returns {Promise<Object>} - The scene elements data
 */
export const fetchSceneElements = async (collectionId, viewMode = '3d') => {
  try {
    console.log(`🎯 Fetching scene elements DIRECTLY for collection ${collectionId} in ${viewMode} mode`);
    
    if (viewMode === '2d') {
      // For 2D view, get walls and unplaced artworks
      const { data: walls, error: wallsError } = await supabase
        .from('scene_elements')
        .select('*')
        .eq('collection_id', collectionId)
        .eq('type', 'wall_2d')
        .eq('visible', true)
        .order('display_order', { ascending: true });
        
      if (wallsError) throw wallsError;
      
      // Get all artwork elements for this collection
      const { data: artworks, error: artworksError } = await supabase
        .from('scene_elements')
        .select('*')
        .eq('collection_id', collectionId)
        .eq('type', 'artwork')
        .eq('visible', true);
        
      if (artworksError) throw artworksError;
      
      // Fetch titles and more data from vcs table for all artworks
      const vcIds = artworks?.map(art => art.vc_id).filter(Boolean) || [];
      let titlesMap = {};
      let acceptedMap = {};
      let nicknameMap = {};
      
      if (vcIds.length > 0) {
        const { data: vcsData, error: vcsError } = await supabase
          .from('vcs')
          .select('vc_id, title, more, uuid')
          .in('vc_id', vcIds);
        
        if (!vcsError && vcsData) {
          titlesMap = vcsData.reduce((acc, item) => {
            acc[item.vc_id] = { title: item.title, more: item.more, uuid: item.uuid };
            return acc;
          }, {});
        }
        
        // Fetch artist nicknames from users table via uuid
        const artistUuids = [...new Set(Object.values(titlesMap).map(v => v.uuid).filter(Boolean))];
        if (artistUuids.length > 0) {
          const { data: usersData, error: usersError } = await supabase
            .from('users')
            .select('uuid, nickname')
            .in('uuid', artistUuids);
          
          if (!usersError && usersData) {
            nicknameMap = usersData.reduce((acc, item) => {
              acc[item.uuid] = item.nickname;
              return acc;
            }, {});
          }
        }
        
        // Fetch accepted status from collection_vc_mapping
        const { data: mappingData, error: mappingError } = await supabase
          .from('collection_vc_mapping')
          .select('vc_id, accepted')
          .eq('collection_id', collectionId)
          .in('vc_id', vcIds);
        
        if (!mappingError && mappingData) {
          acceptedMap = mappingData.reduce((acc, item) => {
            acc[item.vc_id] = item.accepted;
            return acc;
          }, {});
        }
      }
      
      // Add titles, more data, accepted status, and artist nickname to artwork objects
      const artworksWithTitle = artworks?.map(art => {
        const vcsInfo = titlesMap[art.vc_id];
        return {
          ...art,
          title: vcsInfo?.title || null,
          more: vcsInfo?.more || null,
          accepted: acceptedMap[art.vc_id] !== undefined ? acceptedMap[art.vc_id] : null,
          artist_name: vcsInfo?.uuid ? nicknameMap[vcsInfo.uuid] || null : null
        };
      }) || [];
      
      console.log(`✅ Direct query successful (2D): ${walls?.length || 0} walls, ${artworksWithTitle?.length || 0} artworks`);
      
      // Organize artworks: on walls vs unplaced
      const wallsWithArtwork = walls?.map(wall => ({
        ...wall,
        artwork: artworksWithTitle?.filter(art => art.parent_element_id === wall.element_id) || []
      })) || [];
      
      const unplacedArtworks = artworksWithTitle?.filter(art => !art.parent_element_id) || [];
      
      // Fetch easels for this collection
      const { data: easelElements, error: easelsError } = await supabase
        .from('scene_elements')
        .select('*')
        .eq('collection_id', collectionId)
        .eq('type', 'easel')
        .eq('visible', true);
      
      if (easelsError) console.warn('Error fetching easels:', easelsError);
      
      // Attach artwork to each easel (artwork whose parent_element_id matches the easel)
      const easelIds = (easelElements || []).map(e => e.element_id);
      const easelsWithArtwork = (easelElements || []).map(easel => ({
        ...easel,
        artwork: artworksWithTitle?.filter(art => art.parent_element_id === easel.element_id) || []
      }));

      console.log(`📊 Organized: ${wallsWithArtwork.length} walls with artwork, ${unplacedArtworks.length} unplaced artworks, ${easelsWithArtwork.length} easels`);
      
      // Fetch structures for this collection
      const { data: structureElements, error: structuresError } = await supabase
        .from('scene_elements')
        .select('*')
        .eq('collection_id', collectionId)
        .eq('type', 'structure')
        .eq('visible', true);
      
      if (structuresError) console.warn('Error fetching structures:', structuresError);

      return { 
        walls: wallsWithArtwork, 
        unplaced: unplacedArtworks,
        easels: easelsWithArtwork,
        structures: structureElements || []
      };
    } else {
      // For 3D view, get all scene elements (this is what's working!)
      const { data: sceneElements, error } = await supabase
        .from('scene_elements')
        .select('*')
        .eq('collection_id', collectionId)
        .eq('visible', true)
        .order('type', { ascending: true })
        .order('element_id', { ascending: true });
        
      if (error) throw error;
      
      console.log(`✅ Direct query successful (3D): ${sceneElements?.length || 0} total elements`);
      
      // Log element breakdown for debugging
      const elementCounts = {};
      sceneElements?.forEach(el => {
        elementCounts[el.type] = (elementCounts[el.type] || 0) + 1;
      });
      console.log('📊 Element breakdown:', elementCounts);
      
      // Validate data quality
      const validArtworks = sceneElements.filter(el => 
        el.type === 'artwork' && 
        el.source && 
        el.position_x !== null && 
        el.position_y !== null && 
        el.position_z !== null
      );
      
      console.log(`🎨 Valid artworks: ${validArtworks.length} out of ${sceneElements.filter(el => el.type === 'artwork').length}`);
      
      return sceneElements || [];
    }
    
  } catch (error) {
    console.error('💥 Error in direct scene elements fetch:', error);
    throw new Error(`Failed to fetch scene elements directly: ${error.message}`);
  }
};
/**

 * Fetch scene elements directly from Supabase (bypassing API for now)
 * @param {string} collectionId - The collection ID to fetch scene elements for
 * @param {string} viewMode - The view mode (3d or 2d)
 * @returns {Promise<Object>} - The scene elements data
 */
export const fetchSceneElementsDirect = async (collectionId, viewMode = '3d') => {
  try {
    console.log(`🎯 Fetching scene elements DIRECTLY for collection ${collectionId} in ${viewMode} mode`);
    
    if (viewMode === '2d') {
      // For 2D view, get walls and unplaced artworks
      const { data: walls, error: wallsError } = await supabase
        .from('scene_elements')
        .select('*')
        .eq('collection_id', collectionId)
        .eq('type', 'wall_2d')
        .eq('visible', true)
        .order('display_order', { ascending: true });
        
      if (wallsError) throw wallsError;
      
      // Get all artwork elements for this collection
      const { data: artworks, error: artworksError } = await supabase
        .from('scene_elements')
        .select('*')
        .eq('collection_id', collectionId)
        .eq('type', 'artwork')
        .eq('visible', true);
        
      if (artworksError) throw artworksError;
      
      // Fetch titles and more data from vcs table for all artworks
      const vcIds = artworks?.map(art => art.vc_id).filter(Boolean) || [];
      let titlesMap = {};
      let acceptedMap = {};
      let nicknameMap = {};
      
      if (vcIds.length > 0) {
        const { data: vcsData, error: vcsError } = await supabase
          .from('vcs')
          .select('vc_id, title, more, uuid')
          .in('vc_id', vcIds);
        
        if (!vcsError && vcsData) {
          titlesMap = vcsData.reduce((acc, item) => {
            acc[item.vc_id] = { title: item.title, more: item.more, uuid: item.uuid };
            return acc;
          }, {});
        }
        
        // Fetch artist nicknames from users table via uuid
        const artistUuids = [...new Set(Object.values(titlesMap).map(v => v.uuid).filter(Boolean))];
        if (artistUuids.length > 0) {
          const { data: usersData, error: usersError } = await supabase
            .from('users')
            .select('uuid, nickname')
            .in('uuid', artistUuids);
          
          if (!usersError && usersData) {
            nicknameMap = usersData.reduce((acc, item) => {
              acc[item.uuid] = item.nickname;
              return acc;
            }, {});
          }
        }
        
        // Fetch accepted status from collection_vc_mapping
        const { data: mappingData, error: mappingError } = await supabase
          .from('collection_vc_mapping')
          .select('vc_id, accepted')
          .eq('collection_id', collectionId)
          .in('vc_id', vcIds);
        
        if (!mappingError && mappingData) {
          acceptedMap = mappingData.reduce((acc, item) => {
            acc[item.vc_id] = item.accepted;
            return acc;
          }, {});
        }
      }
      
      // Add titles, more data, accepted status, and artist nickname to artwork objects
      const artworksWithTitle = artworks?.map(art => {
        const vcsInfo = titlesMap[art.vc_id];
        return {
          ...art,
          title: vcsInfo?.title || null,
          more: vcsInfo?.more || null,
          accepted: acceptedMap[art.vc_id] !== undefined ? acceptedMap[art.vc_id] : null,
          artist_name: vcsInfo?.uuid ? nicknameMap[vcsInfo.uuid] || null : null
        };
      }) || [];
      
      console.log(`✅ Direct query successful (2D): ${walls?.length || 0} walls, ${artworksWithTitle?.length || 0} artworks`);
      
      return { 
        walls: walls || [], 
        unplaced: artworksWithTitle || [] 
      };
    } else {
      // For 3D view, get all scene elements (this is what's working!)
      const { data: sceneElements, error } = await supabase
        .from('scene_elements')
        .select('*')
        .eq('collection_id', collectionId)
        .eq('visible', true)
        .order('type', { ascending: true })
        .order('element_id', { ascending: true });
        
      if (error) throw error;
      
      console.log(`✅ Direct query successful (3D): ${sceneElements?.length || 0} total elements`);
      
      // Log element breakdown for debugging
      const elementCounts = {};
      sceneElements?.forEach(el => {
        elementCounts[el.type] = (elementCounts[el.type] || 0) + 1;
      });
      console.log('📊 Element breakdown:', elementCounts);
      
      // Validate data quality
      const validArtworks = sceneElements.filter(el => 
        el.type === 'artwork' && 
        el.source && 
        el.position_x !== null && 
        el.position_y !== null && 
        el.position_z !== null
      );
      
      console.log(`🎨 Valid artworks: ${validArtworks.length} out of ${sceneElements.filter(el => el.type === 'artwork').length}`);
      
      return sceneElements || [];
    }
    
  } catch (error) {
    console.error('💥 Error in direct scene elements fetch:', error);
    throw new Error(`Failed to fetch scene elements directly: ${error.message}`);
  }
};

/**
 * Distribute artworks in a collection across a grid pattern in 3D space
 * @param {number} collectionId - The collection ID to distribute artworks for
 * @returns {Promise<Object>} - Result of the distribution operation
 */
export const distributeCollectionArtworks = async (collectionId) => {
  try {
    console.log(`Distributing artworks for collection ${collectionId}`);
    
    // The SQL query for distributing artworks
    const distributeSQL = `
      -- Get all accepted artworks for the collection and calculate grid positions
      WITH 
      accepted_artworks AS (
        SELECT 
          v.vc_id,
          v.cover_image,
          v.width,
          v.height,
          c.order_num,
          c.created_at,
          ROW_NUMBER() OVER (ORDER BY c.order_num, c.created_at) - 1 AS position_index
        FROM collection_vc_mapping c
        JOIN vcs v ON c.vc_id = v.vc_id
        WHERE c.collection_id = $1
          AND c.accepted = TRUE
      ),
      
      grid_positions AS (
        SELECT 
          vc_id,
          cover_image,
          width,
          height,
          -- Grid configuration (8 artworks per row)
          (-280 + (position_index % 8) * 80) AS position_x,
          65 AS position_y,
          (-50 - (position_index / 8) * 100) AS position_z
        FROM accepted_artworks
      )
      
      -- Insert or update scene elements
      INSERT INTO scene_elements (
        vc_id,
        type,
        source,
        position_x,
        position_y,
        position_z,
        rotation_x,
        rotation_y,
        rotation_z,
        width,
        height,
        collection_id,
        visible,
        opacity,
        created_at,
        updated_at
      )
      SELECT 
        gp.vc_id,
        'artwork',
        gp.cover_image,
        gp.position_x,
        gp.position_y,
        gp.position_z,
        0, 0, 0,
        gp.width,
        gp.height,
        $1,
        true,
        1.0,
        NOW(),
        NOW()
      FROM grid_positions gp
      ON CONFLICT (vc_id, collection_id, type) 
      DO UPDATE SET
        source = EXCLUDED.source,
        position_x = EXCLUDED.position_x,
        position_y = EXCLUDED.position_y,
        position_z = EXCLUDED.position_z,
        width = EXCLUDED.width,
        height = EXCLUDED.height,
        visible = EXCLUDED.visible,
        updated_at = NOW()
      RETURNING vc_id, position_x, position_y, position_z;
    `;
    
    // Execute the distribution query
    const { data: distributedElements, error: distributeError } = await supabase
      .rpc('execute_sql', { 
        sql_query: distributeSQL,
        params: [collectionId] 
      });
    
    if (distributeError) {
      // Fallback to direct query if RPC fails
      console.log('RPC failed, using direct query...');
      
      const { data, error } = await supabase
        .from('scene_elements')
        .select('vc_id')
        .eq('collection_id', collectionId)
        .eq('type', 'artwork');
        
      if (error) throw error;
      
      // For fallback, we'll execute the parts separately
      // First get accepted artworks
      const { data: artworks, error: artworkError } = await supabase
        .from('collection_vc_mapping')
        .select(`
          vc_id,
          order_num,
          created_at,
          vcs!inner(vc_id, cover_image, width, height)
        `)
        .eq('collection_id', collectionId)
        .eq('accepted', true)
        .order('order_num', { ascending: true })
        .order('created_at', { ascending: true });
        
      if (artworkError) throw artworkError;
      
      // Calculate positions and update/insert elements
      const updates = [];
      const inserts = [];
      
      for (let i = 0; i < artworks.length; i++) {
        const artwork = artworks[i];
        const row = Math.floor(i / 8);
        const col = i % 8;
        
        const elementData = {
          vc_id: artwork.vc_id,
          type: 'artwork',
          source: artwork.vcs.cover_image,
          position_x: -280 + (col * 80),
          position_y: 65,
          position_z: -50 - (row * 100),
          rotation_x: 0,
          rotation_y: 0,
          rotation_z: 0,
          width: artwork.vcs.width,
          height: artwork.vcs.height,
          collection_id: collectionId,
          visible: true,
          opacity: 1.0,
          updated_at: new Date().toISOString()
        };
        
        // Check if element already exists
        const existingElement = data.find(e => e.vc_id === artwork.vc_id);
        if (existingElement) {
          updates.push(elementData);
        } else {
          inserts.push({
            ...elementData,
            created_at: new Date().toISOString()
          });
        }
      }
      
      // Execute updates
      if (updates.length > 0) {
        for (const update of updates) {
          const { error: updateError } = await supabase
            .from('scene_elements')
            .update(update)
            .eq('vc_id', update.vc_id)
            .eq('collection_id', collectionId)
            .eq('type', 'artwork');
            
          if (updateError) console.error('Update error:', updateError);
        }
      }
      
      // Execute inserts
      if (inserts.length > 0) {
        const { error: insertError } = await supabase
          .from('scene_elements')
          .insert(inserts);
          
        if (insertError) console.error('Insert error:', insertError);
      }
      
      return {
        success: true,
        message: `Successfully distributed ${artworks.length} artworks`,
        updated: updates.length,
        inserted: inserts.length
      };
    }
    
    // Clean up unaccepted artworks
    const { error: cleanupError } = await supabase
      .from('scene_elements')
      .delete()
      .eq('collection_id', collectionId)
      .eq('type', 'artwork')
      .in('vc_id', 
        supabase
          .from('collection_vc_mapping')
          .select('vc_id')
          .eq('collection_id', collectionId)
          .eq('accepted', false)
      );
      
    if (cleanupError) {
      console.warn('Cleanup error:', cleanupError);
    }
    
    return {
      success: true,
      message: `Successfully distributed artworks for collection ${collectionId}`,
      elements: distributedElements
    };
    
  } catch (error) {
    console.error('Error distributing collection artworks:', error);
    throw new Error(`Failed to distribute artworks: ${error.message}`);
  }
};

/**
 * Fetch art walls for a collection
 * @param {string} collectionId - The collection ID
 * @returns {Promise<Array>} - Array of wall elements
 */
export const fetchCollectionWalls = async (collectionId) => {
  try {
    const { data, error } = await supabase
      .from('scene_elements')
      .select('*')
      .eq('collection_id', collectionId)
      .eq('type', 'wall_2d')
      .order('display_order', { ascending: true });
      
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching walls:', error);
    throw error;
  }
};

/**
 * Fetch artwork elements for a collection
 * @param {string} collectionId - The collection ID
 * @returns {Promise<Array>} - Array of artwork elements
 */
export const fetchArtworkElements = async (collectionId) => {
  try {
    const { data, error } = await supabase
      .from('scene_elements')
      .select('*')
      .eq('collection_id', collectionId)
      .eq('type', 'artwork')
      .eq('visible', true);
      
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching artwork elements:', error);
    throw error;
  }
};

/**
 * Update scene element display location text
 * @param {number} elementId - The scene element ID
 * @param {string} displayText - The text to display
 * @returns {Promise<Object>} - Updated element data
 */
export const updateSceneElementText = async (elementId, displayText) => {
  try {
    const { data, error } = await supabase
      .from('scene_elements')
      .update({ text_content: displayText })
      .eq('element_id', elementId)
      .select();
      
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating scene element text:', error);
    throw error;
  }
};

/**
 * Fetch collection artworks
 * @param {string} collectionId - The collection ID
 * @returns {Promise<Array>} - Array of artworks with their scene elements data
 */
export const fetchCollectionArtworks = async (collectionId) => {
  try {
    // Get the mapping between collection and artworks - only accepted artworks
    const { data: mappings, error: mappingError } = await supabase
      .from('collection_vc_mapping')
      .select('vc_id')
      .eq('collection_id', collectionId)
      .eq('accepted', true);
    
    if (mappingError) throw mappingError;
    
    if (!mappings || mappings.length === 0) {
      return [];
    }
    
    // Get the artwork IDs
    const artworkIds = mappings.map(mapping => mapping.vc_id);
    
    // Fetch artwork details
    const { data: artworks, error: artworksError } = await supabase
      .from('vcs')
      .select(`
        vc_id, 
        title, 
        description, 
        cover_image, 
        uuid, 
        price,
        status,
        width,
        height,
        depth,
        audio_story,
        artwork_completed_date
      `)
      .in('vc_id', artworkIds);
    
    if (artworksError) throw artworksError;
    
    // Get artist info for artworks
    const artistIds = artworks
      .map(artwork => artwork.uuid)
      .filter(Boolean);
      
    const { data: artists, error: artistsError } = await supabase
      .from('users')
      .select('uuid, nickname, first_name, last_name')
      .in('uuid', artistIds);
      
    if (artistsError) throw artistsError;
    
    // Get scene elements data for the artworks
    const { data: sceneElements, error: sceneError } = await supabase
      .from('scene_elements')
      .select('*')
      .eq('collection_id', collectionId)
      .eq('type', 'artwork')
      .in('vc_id', artworkIds);
      
    if (sceneError) throw sceneError;
    
    // Create mapping from vc_id to scene element
    const sceneElementsByVcId = {};
    sceneElements.forEach(element => {
      sceneElementsByVcId[element.vc_id] = element;
    });
    
    // Combine artwork data with artist info and scene element data
    const enrichedArtworks = artworks.map(artwork => {
      const artist = artists.find(a => a.uuid === artwork.uuid) || {};
      const sceneElement = sceneElementsByVcId[artwork.vc_id] || {};
      const rawDescription = (artwork.description || '').trim();
      let medium = rawDescription ? rawDescription.slice(0, 30) : null;
      if (medium && rawDescription.length > 30) {
        medium = `${medium}…`;
      }
      
      return {
        ...artwork,
        medium,
        artwork_completed_date: artwork.artwork_completed_date || null,
        artist_name: artist.nickname || 
          `${artist.first_name || ''} ${artist.last_name || ''}`.trim() || 
          'Unknown Artist',
        // Include scene element data
        element_id: sceneElement.element_id,
        parent_element_id: sceneElement.parent_element_id,
        wall_position_x: sceneElement.wall_position_x,
        wall_position_y: sceneElement.wall_position_y,
        display_location: sceneElement.text_content || ''
      };
    });
    
    return enrichedArtworks;
    
  } catch (error) {
    console.error('Error fetching collection artworks:', error);
    throw error;
  }
};

/**
 * Fetch collection details
 * @param {string} collectionId - The collection ID
 * @returns {Promise<Object>} - Collection data
 */
export const fetchCollectionDetails = async (collectionId) => {
  try {
    const { data, error } = await supabase
      .from('collections')
      .select('*')
      .eq('collection_id', collectionId)
      .single();
      
    if (error) throw error;
    return data;
    
  } catch (error) {
    console.error('Error fetching collection details:', error);
    throw error;
  }
};

/**
 * Update artwork position on wall
 * @param {number} artworkId - The artwork element ID
 * @param {number|null} wallId - The wall element ID (null if unplaced)
 * @param {number} posX - X position in inches
 * @param {number} posY - Y position in inches
 * @returns {Promise<Object>} - Updated element data
 */
export const updateArtworkPosition = async (artworkId, wallId, posX, posY) => {
  try {
    // Base API endpoint - use environment variable if available
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
    const updateEndpoint = `${apiBaseUrl}/update_scene_element`;
    
    console.log(`Sending update for artwork ${artworkId} to wall ${wallId} at (${posX}, ${posY})`);
    
    const response = await fetch(updateEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        update_mode: "wall_placement",
        element_id: artworkId,
        parent_element_id: wallId,
        position_x: posX,
        position_y: posY
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Server returned error: ${errorText}`);
    }
    
    const responseData = await response.json();
    return responseData;
    
  } catch (error) {
    console.error('Failed to update position:', error);
    
    // Fallback to direct Supabase update if API fails
    try {
      console.log('Attempting fallback to direct Supabase update...');
      
      const { data, error: updateError } = await supabase
        .from('scene_elements')
        .update({
          parent_element_id: wallId,
          wall_position_x: posX,
          wall_position_y: posY
        })
        .eq('element_id', artworkId)
        .select();
        
      if (updateError) throw updateError;
      return data;
    } catch (fallbackError) {
      console.error('Fallback update also failed:', fallbackError);
      throw error; // Throw the original error
    }
  }
};

/**
 * Add a new wall
 * @param {string} collectionId - The collection ID
 * @param {string} name - Wall name
 * @param {number} width - Wall width in inches
 * @param {number} height - Wall height in inches
 * @returns {Promise<Object>} - New wall data
 */
export const addWall = async (collectionId, name, width, height) => {
  try {
    console.log('🔧 Adding wall directly with Supabase client...');
    
    // Get the highest display_order for this collection
    const { data: walls } = await supabase
      .from('scene_elements')
      .select('display_order')
      .eq('collection_id', collectionId)
      .eq('type', 'wall_2d')
      .order('display_order', { ascending: false })
      .limit(1);
      
    // Get the next order value
    const nextOrder = walls && walls.length > 0 && walls[0].display_order !== null
      ? walls[0].display_order + 1
      : 0;
    
    console.log('🔧 Next display order:', nextOrder);
    
    // Insert new wall
    const { data, error } = await supabase
      .from('scene_elements')
      .insert({
        collection_id: collectionId,
        type: 'wall_2d',
        source: name || 'Untitled Wall',
        width: parseFloat(width),
        height: parseFloat(height),
        visible: true,
        display_order: nextOrder
      })
      .select()
      .single();
      
    if (error) {
      console.error('🔧 Database error:', error);
      throw error;
    }
    
    console.log('🔧 Successfully added wall to database:', data);
    return { 
      message: "Wall added successfully!", 
      wall: data 
    };
    
  } catch (error) {
    console.error('Error adding wall:', error);
    throw error;
  }
};

/**
 * Pod templates: predefined configurations of walls forming a pod unit.
 * Think of each template as a flat wall that gets folded at 90° joints.
 * Each panel has offsets relative to the fold/center point (in inches).
 * Artwork goes on the front face of each panel.
 */
/**
 * A pod is a single wall sheet folded at the center to form an L-shape.
 * Default: 96"×96" (8ft×8ft) folds into two 48"×96" panels at 90°.
 * The fold point is the pod center. Panel A extends along +X, Panel B along -Z.
 */
export const POD_TEMPLATES = {
  'l-shape': {
    name: 'L-Shape (Folded Wall)',
    description: 'One wall folded at center — two panels at 90° forming an L'
  }
};

/**
 * Add a pod (folded wall) to a collection.
 * One wall sheet folded at center creates an L-shape with two panels.
 * Panel A extends along +X from the fold, Panel B extends along -Z.
 * @param {number} collectionId
 * @param {string} podName - Base name (e.g., "Pod1"). Panels named Pod1-1, Pod1-2.
 * @param {number} centerX - Fold point X in inches
 * @param {number} centerZ - Fold point Z in inches
 * @param {number} rotationY - Overall rotation in degrees
 * @param {number} wallWidth - Total wall width before folding (default 96" = 8ft)
 * @param {number} wallHeight - Wall height (default 96" = 8ft)
 */
export const addPod = async (collectionId, podName, centerX = 0, centerZ = 0, rotationY = 0, wallWidth = 96, wallHeight = 96, tiltAngle = 70) => {
  try {
    // Pod = single tilted wall panel (one side of an A-frame tent)
    // tiltAngle is degrees from horizontal (70° = steep lean, 90° = vertical)
    const tiltRad = (tiltAngle * Math.PI) / 180;
    const floorDepth = wallHeight * Math.cos(tiltRad); // footprint depth on the floor
    const wallCenterY = (wallHeight * Math.sin(tiltRad)) / 2; // center height when tilted

    console.log(`🏗️ Creating pod "${podName}" (${wallWidth}"×${wallHeight}" tilted ${tiltAngle}°) at (${centerX}, ${centerZ})`);

    // Get the highest display_order for this collection
    const { data: existingWalls } = await supabase
      .from('scene_elements')
      .select('display_order')
      .eq('collection_id', collectionId)
      .eq('type', 'wall_2d')
      .order('display_order', { ascending: false })
      .limit(1);

    let nextOrder = existingWalls && existingWalls.length > 0 && existingWalls[0].display_order !== null
      ? existingWalls[0].display_order + 1
      : 0;

    const wallInsert = {
      collection_id: collectionId,
      type: 'wall_2d',
      source: podName,
      width: wallWidth,
      height: wallHeight,
      wall_center_x: centerX,
      wall_center_y: wallCenterY,
      wall_center_z: centerZ,
      wall_rotation_y: rotationY,
      visible: true,
      display_order: nextOrder,
      text_content: JSON.stringify({
        pod_group: podName,
        template: 'tent-panel',
        tilt_angle: tiltAngle,
        wallWidth,
        wallHeight
      })
    };

    const { data: walls, error } = await supabase
      .from('scene_elements')
      .insert([wallInsert])
      .select();

    if (error) {
      console.error('🏗️ Error creating pod wall:', error);
      throw error;
    }

    console.log(`✅ Pod "${podName}" created — ${wallWidth}"×${wallHeight}" tilted ${tiltAngle}° (floor depth: ${floorDepth.toFixed(1)}")`);
    return {
      message: `Pod "${podName}" created — ${wallWidth}"×${wallHeight}" tilted ${tiltAngle}°`,
      podName,
      walls
    };
  } catch (error) {
    console.error('Error adding pod:', error);
    throw error;
  }
};

/**
 * Add an easel to a collection.
 * @param {number} collectionId - Collection ID
 * @param {string} name - Easel name
 * @param {number} centerX - Position X in inches
 * @param {number} centerZ - Position Z in inches
 * @param {number} rotationY - Facing direction in degrees
 * @param {number} displayWidth - Max artwork display width in inches (default 24)
 * @param {number} displayHeight - Max artwork display height in inches (default 36)
 * @returns {Promise<Object>} - Created easel
 */
export const addEasel = async (collectionId, name, centerX = 0, centerZ = 0, rotationY = 0) => {
  try {
    console.log(`🎨 Adding easel "${name}" at (${centerX}, ${centerZ})`);

    const { data, error } = await supabase
      .from('scene_elements')
      .insert({
        collection_id: collectionId,
        type: 'easel',
        source: name || 'Untitled Easel',
        position_x: centerX,
        position_y: 60, // Centerline height in inches (eye level default)
        position_z: centerZ,
        rotation_y: rotationY,
        visible: true
      })
      .select()
      .single();

    if (error) {
      console.error('🎨 Error creating easel:', error);
      throw error;
    }

    console.log('✅ Easel created:', data);
    return {
      message: 'Easel added successfully!',
      easel: data
    };
  } catch (error) {
    console.error('Error adding easel:', error);
    throw error;
  }
};

/**
 * Add a structural element (column, solid wall, etc.) — these never hold artwork
 * @param {string} collectionId
 * @param {string} name - Display name
 * @param {string} shape - column-square | column-round | column-rect | wall-solid
 * @param {number} width - Width in inches
 * @param {number} height - Height in inches
 * @param {number} depth - Depth in inches
 * @param {number} centerX - Position X in inches
 * @param {number} centerZ - Position Z in inches
 * @param {number} rotationY - Rotation in degrees
 * @param {string} color - Hex color
 * @returns {Promise<Object>}
 */
export const addStructure = async (collectionId, name, shape, width, height, depth, centerX = 0, centerZ = 0, rotationY = 0, color = '#9ca3af') => {
  try {
    console.log(`🏗️ Adding structure "${name}" (${shape}) at (${centerX}, ${centerZ})`);

    const { data, error } = await supabase
      .from('scene_elements')
      .insert({
        collection_id: collectionId,
        type: 'structure',
        source: name || 'Untitled Structure',
        width,
        height,
        depth,
        position_x: centerX,
        position_y: 0,
        position_z: centerZ,
        rotation_y: rotationY,
        color: color,
        text_content: JSON.stringify({ shape }),
        visible: true
      })
      .select()
      .single();

    if (error) {
      console.error('🏗️ Error creating structure:', error);
      throw error;
    }

    console.log('✅ Structure created:', data);
    return {
      message: 'Structure added successfully!',
      structure: data
    };
  } catch (error) {
    console.error('Error adding structure:', error);
    throw error;
  }
};

/**
 * Update an existing structure element (dimensions, shape, color, position, rotation)
 * @param {Object} payload - Fields to update
 * @returns {Promise<Object>} - Updated structure data
 */
export const updateStructure = async (payload) => {
  try {
    const { element_id, name, shape, width, height, depth, position_x, position_y, position_z, rotation_y, color } = payload;
    console.log(`🏗️ Updating structure ${element_id}`);

    const updateFields = {};
    if (name !== undefined) updateFields.source = name;
    if (width !== undefined) updateFields.width = width;
    if (height !== undefined) updateFields.height = height;
    if (depth !== undefined) updateFields.depth = depth;
    if (position_x !== undefined) updateFields.position_x = position_x;
    if (position_y !== undefined) updateFields.position_y = position_y;
    if (position_z !== undefined) updateFields.position_z = position_z;
    if (rotation_y !== undefined) updateFields.rotation_y = rotation_y;
    if (color !== undefined) updateFields.color = color;
    if (shape !== undefined) {
      // Preserve existing text_content fields (like opacity) when updating shape
      let existingMeta = {};
      if (payload.text_content) {
        try { existingMeta = JSON.parse(payload.text_content); } catch { existingMeta = {}; }
      }
      existingMeta.shape = shape;
      updateFields.text_content = JSON.stringify(existingMeta);
    } else if (payload.text_content !== undefined) {
      updateFields.text_content = payload.text_content;
    }

    const { data, error } = await supabase
      .from('scene_elements')
      .update(updateFields)
      .eq('element_id', element_id)
      .select()
      .single();

    if (error) throw error;
    console.log('✅ Structure updated:', data);
    return data;
  } catch (error) {
    console.error('Error updating structure:', error);
    throw error;
  }
};

/**
 * Update an existing wall
 * @param {number} wallId - Wall element ID
 * @param {string} name - Wall name
 * @param {number} width - Wall width in inches
 * @param {number} height - Wall height in inches
 * @param {number} wallCenterX - Wall center X position in inches (optional)
 * @param {number} wallCenterY - Wall center Y position in inches (optional)
 * @param {number} wallCenterZ - Wall center Z position in inches (optional)
 * @param {number} wallRotationY - Wall rotation Y in degrees (optional)
 * @param {number} displayOrder - Wall display order (optional)
 * @returns {Promise<Object>} - Updated wall data
 */
export const updateWall = async (wallId, name, width, height, wallCenterX, wallCenterY, wallCenterZ, wallRotationY, displayOrder, backgroundColor, materialType, textContent) => {
  try {
    console.log('🔧 Updating wall directly with Supabase client...');
    
    const updateData = {
      source: name || 'Untitled Wall',
      width: parseFloat(width),
      height: parseFloat(height)
    };
    
    // Add 3D positioning data if provided
    if (wallCenterX !== undefined) updateData.wall_center_x = parseFloat(wallCenterX);
    if (wallCenterY !== undefined) updateData.wall_center_y = parseFloat(wallCenterY);
    if (wallCenterZ !== undefined) updateData.wall_center_z = parseFloat(wallCenterZ);
    if (wallRotationY !== undefined) updateData.wall_rotation_y = parseFloat(wallRotationY);
    if (displayOrder !== undefined) updateData.display_order = parseInt(displayOrder);
    if (backgroundColor !== undefined) updateData.background_color = backgroundColor;
    if (materialType !== undefined) updateData.color = materialType;
    if (textContent !== undefined) updateData.text_content = textContent;
    
    console.log('🔧 Update data:', updateData);
    console.log('🔧 Wall ID:', wallId);
    
    const { data, error } = await supabase
      .from('scene_elements')
      .update(updateData)
      .eq('element_id', wallId)
      .eq('type', 'wall_2d')
      .select()
      .single();
      
    if (error) {
      console.error('🔧 Database error:', error);
      throw error;
    }
    
    console.log('🔧 Successfully updated wall in database:', data);
    return { 
      message: "Wall updated successfully!", 
      wall: data 
    };
    
  } catch (error) {
    console.error('Error updating wall:', error);
    throw error;
  }
};

/**
 * Delete a wall
 * @param {number} wallId - Wall element ID
 * @returns {Promise<Object>} - Response data
 */
export const deleteWall = async (wallId) => {
  try {
    // Base API endpoint - use environment variable if available
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
    const updateEndpoint = `${apiBaseUrl}/update_scene_element`;
    
    const response = await fetch(updateEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        update_mode: "delete_wall",
        element_id: wallId
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to delete wall: ${errorText}`);
    }
    
    return await response.json();
    
  } catch (error) {
    console.error('Error deleting wall:', error);
    
    // Fallback to direct Supabase delete if API fails
    try {
      console.log('Attempting fallback to direct Supabase delete...');
      
      const { error: deleteError } = await supabase
        .from('scene_elements')
        .delete()
        .eq('element_id', wallId)
        .eq('type', 'wall_2d');
        
      if (deleteError) throw deleteError;
      
      return { message: 'Wall deleted successfully' };
    } catch (fallbackError) {
      console.error('Fallback delete also failed:', fallbackError);
      throw error; // Throw the original error
    }
  }
};

/**
 * Delete a scene element (easel, structure, etc.) by element_id
 */
export const deleteSceneElement = async (elementId) => {
  const { error } = await supabase
    .from('scene_elements')
    .delete()
    .eq('element_id', elementId);
  if (error) throw error;
  return { message: 'Element deleted' };
};

/**
 * Update wall order
 * @param {string} collectionId - Collection ID
 * @param {Array} walls - Array of wall objects with element_id
 * @returns {Promise<Object>} - Response data
 */
export const updateWallOrder = async (collectionId, walls) => {
  try {
    // Base API endpoint - use environment variable if available
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
    const updateEndpoint = `${apiBaseUrl}/update_scene_element`;
    
    const response = await fetch(updateEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        update_mode: "update_wall_order",
        collection_id: collectionId,
        walls: walls.map(wall => ({
          element_id: wall.element_id
        }))
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update wall order: ${errorText}`);
    }
    
    return await response.json();
    
  } catch (error) {
    console.error('Error updating wall order:', error);
    
    // Fallback to direct Supabase updates if API fails
    try {
      console.log('Attempting fallback to direct Supabase updates...');
      
      // Update each wall's display_order
      const updatePromises = walls.map((wall, index) => {
        return supabase
          .from('scene_elements')
          .update({ display_order: index })
          .eq('element_id', wall.element_id)
          .eq('type', 'wall_2d')
          .eq('collection_id', collectionId);
      });
      
      await Promise.all(updatePromises);
      
      return { 
        message: "Wall order updated successfully!",
        count: walls.length
      };
    } catch (fallbackError) {
      console.error('Fallback updates also failed:', fallbackError);
      throw error; // Throw the original error
    }
  }
};

/**
 * Fetches a paginated and sorted list of all artists and their stats from the database.
 * @param {number} page - The page number to fetch.
 * @param {number} pageSize - The number of items per page.
 * @param {string} sortColumn - The column to sort by.
 * @param {string} sortDirection - The direction to sort ('asc' or 'desc').
 * @returns {Promise<{artists: Array, totalCount: number, verifiedCount: number}>} - Object containing artist data, total count, and verified count.
 */
export const fetchAllArtists = async (page = 1, pageSize = 20, sortColumn = 'nickname', sortDirection = 'asc') => {
  try {
    const { data, error } = await supabase.rpc('get_all_artists_with_stats', {
      page_num: page,
      page_size: pageSize,
      sort_column: sortColumn,
      sort_direction: sortDirection,
    });

    if (error) {
      console.error('Error calling get_all_artists_with_stats RPC:', error);
      throw error;
    }
    
    // The data from the RPC is a single JSON object.
    // We need to parse it and return the component parts.
    return {
      artists: data.artists_on_page || [],
      totalCount: data.total_artists || 0,
      verifiedCount: data.verified_artists || 0,
    };
    
  } catch (error) {
    console.error('An error occurred while fetching artists:', error);
    return { artists: [], totalCount: 0, verifiedCount: 0 };
  }
};

/**
 * Fetch details for a single artist
 * @param {string} artistId - The handle of the artist
 * @returns {Promise<Object>} - The artist's full details
 */
export const fetchArtistDetails = async (artistId) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('handle', artistId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error(`Error fetching details for artist ${artistId}:`, error);
    throw error;
  }
};

/**
 * Fetch all artworks for a given artist
 * @param {string} artistId - The handle of the artist
 * @returns {Promise<Array>} - An array of the artist's artworks
 */
export const fetchArtworksByArtist = async (artistId) => {
  try {
    // First get the artist's uuid using the handle
    const { data: artist, error: artistError } = await supabase
      .from('users')
      .select('uuid')
      .eq('handle', artistId)
      .single();

    if (artistError) throw artistError;

    // Then get artworks using the uuid with gallery information
    const { data, error } = await supabase
      .from('vcs')
      .select(`
        vc_id, 
        title, 
        cover_image, 
        price, 
        description, 
        status,
        location,
        gallery:galleries!public_vcs_location_fkey (
          gallery_id,
          name,
          address,
          type
        )
      `)
      .eq('uuid', artist.uuid);

    if (error) throw error;
    return data;
  } catch (error) {
    console.error(`Error fetching artworks for artist ${artistId}:`, error);
    throw error;
  }
};

/**
 * Fetch artists with search and enhanced info for admin dashboard
 * @param {string} searchQuery - The search string to filter artists
 * @param {number} limit - Number of artists to fetch
 * @param {number} offset - Offset for pagination
 * @returns {Promise<Array>} - Array of artist objects with enhanced info
 */
export const fetchArtistsWithSearch = async (searchQuery = '', limit = 20, offset = 0) => {
  try {
    const { data, error } = await supabase.rpc('get_all_artists_with_search_enhanced', {
      search_query: searchQuery,
      limit_val: limit,
      offset_val: offset,
    });
    if (error) throw error;
    return data?.artists || [];
  } catch (error) {
    console.error('Error fetching artists with search:', error);
    return [];
  }
};

/**
 * Fetch artists with search and enhanced info for admin dashboard (safe version)
 * @param {string} searchQuery - The search string to filter artists
 * @param {number} limit - Number of artists to fetch
 * @param {number} offset - Offset for pagination
 * @returns {Promise<Array>} - Array of artist objects with enhanced info
 */
export const fetchArtistsWithSearchSafe = async (searchQuery = '', limit = 20, offset = 0) => {
  try {
    const { data, error } = await supabase.rpc('get_all_artists_with_search_safe', {
      search_query: searchQuery,
      limit_val: limit,
      offset_val: offset,
    });
    if (error) throw error;
    // Unwrap the response if it's an array with the function name as a key
    if (Array.isArray(data) && data.length > 0 && data[0].get_all_artists_with_search_safe) {
      return data[0].get_all_artists_with_search_safe;
    }
    return data || { artists: [], totals: {} };
  } catch (error) {
    console.error('Error fetching artists with search (safe):', error);
    return { artists: [], totals: {} };
  }
};

/**
 * Fetch artists with search, enhanced info, and sorting for admin dashboard
 * @param {string} searchQuery - The search string to filter artists
 * @param {number} limit - Number of artists to fetch
 * @param {number} offset - Offset for pagination
 * @param {string} sortColumn - Column to sort by (nickname, email, artworkCount, created_at, accountAge)
 * @param {string} sortDirection - Sort direction (asc or desc)
 * @returns {Promise<Object>} - Object with artists array and totals object
 */
export const fetchArtistsWithSearchSafeSorted = async (searchQuery = '', limit = 20, offset = 0, sortColumn = 'created_at', sortDirection = 'desc') => {
  try {
    console.log('=== API FUNCTION START ===');
    console.log('Parameters:', { searchQuery, limit, offset, sortColumn, sortDirection });
    
    const { data, error } = await supabase.rpc('get_all_artists_with_search_safe_sorted', {
      search_query: searchQuery,
      limit_val: limit,
      offset_val: offset,
      sort_column: sortColumn,
      sort_direction: sortDirection,
    });
    
    console.log('=== SUPABASE RESPONSE ===');
    console.log('Error:', error);
    console.log('Raw data:', data);
    
    if (error) throw error;
    
    // Use the same simple unwrapping logic as the working function
    if (Array.isArray(data) && data.length > 0 && data[0].get_all_artists_with_search_safe_sorted) {
      return data[0].get_all_artists_with_search_safe_sorted;
    }
    return data || { artists: [], totals: {} };
    
  } catch (error) {
    console.error('=== API ERROR ===', error);
    return { artists: [], totals: {} };
  }
};

/**
 * Update a scene element's position and rotation
 * @param {Object} payload - { vc_id, element_id, position_x, position_y, position_z, rotation_x, rotation_y, rotation_z }
 * @returns {Promise<Object>} - Updated element data
 */
export const updateSceneElement = async (payload) => {
  try {
    const { vc_id, element_id, position_x, position_y, position_z, rotation_x, rotation_y, rotation_z } = payload;
    const idField = vc_id ? { vc_id } : { element_id };
    const { data, error } = await supabase
      .from('scene_elements')
      .update({
        position_x,
        position_y,
        position_z,
        rotation_x,
        rotation_y,
        rotation_z
      })
      .match(idField)
      .select()
      .single();
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating scene element:', error);
    throw error;
  }
};

/**
 * Update artwork dimensions (width/height) in the vcs table
 * @param {string} vcId - The vc_id of the artwork
 * @param {number} width - New width in inches
 * @param {number} height - New height in inches
 * @param {number|null} hangerHeight - Optional hanger height from top of artwork in inches
 * @returns {Promise<Object>} - Updated artwork data
 */
export const updateArtworkDimensions = async (vcId, width, height, hangerHeight = null) => {
  try {
    console.log(`Updating artwork dimensions for vc_id=${vcId}: ${width}" × ${height}"${hangerHeight ? `, hanger: ${hangerHeight}"` : ''}`);
    
    // Build the update object
    const updateData = {
      width: parseFloat(width),
      height: parseFloat(height)
    };
    
    // If hangerHeight is provided, we need to update the 'more' JSONB field
    // First, get the current 'more' value to merge with it
    if (hangerHeight !== null) {
      const { data: currentVc, error: fetchError } = await supabase
        .from('vcs')
        .select('more')
        .eq('vc_id', vcId)
        .single();
      
      if (fetchError) {
        console.error('Error fetching current more field:', fetchError);
        throw fetchError;
      }
      
      // Merge hanger_height into existing more JSONB
      const currentMore = currentVc?.more || { process: '', inspiration: '' };
      updateData.more = {
        ...currentMore,
        hanger_height: parseFloat(hangerHeight)
      };
    }
    
    const { data, error } = await supabase
      .from('vcs')
      .update(updateData)
      .eq('vc_id', vcId)
      .select()
      .single();
      
    if (error) {
      console.error('Error updating artwork dimensions:', error);
      throw error;
    }
    
    console.log('Artwork dimensions updated:', data);
    return data;
  } catch (error) {
    console.error('Failed to update artwork dimensions:', error);
    throw new Error(`Failed to update artwork dimensions: ${error.message}`);
  }
};

/**
 * Swap artwork width and height in both vcs and scene_elements tables
 */
export const swapArtworkDimensions = async (vcId, elementId, currentWidth, currentHeight) => {
  try {
    const newWidth = parseFloat(currentHeight);
    const newHeight = parseFloat(currentWidth);

    const { error: vcsError } = await supabase
      .from('vcs')
      .update({ width: newWidth, height: newHeight })
      .eq('vc_id', vcId);

    if (vcsError) throw vcsError;

    if (elementId) {
      const { error: seError } = await supabase
        .from('scene_elements')
        .update({ width: newWidth, height: newHeight })
        .eq('element_id', elementId);

      if (seError) throw seError;
    }

    return { width: newWidth, height: newHeight };
  } catch (error) {
    console.error('Failed to swap artwork dimensions:', error);
    throw error;
  }
};

/**
 * Update collection artwork metadata conservatively - only update source, width, height
 * Add new artworks and remove ones no longer in collection
 * Preserves all positioning data (3D and wall positions)
 * @param {string} collectionId - The collection ID
 * @returns {Promise<Object>} - Update result
 */
export const updateCollectionArtworkMetadata = async (collectionId) => {
  try {
    console.log(`Updating artwork metadata for collection ${collectionId}`);
    
    // Get all accepted artworks from collection
    const { data: acceptedArtworks, error: artworkError } = await supabase
      .from('collection_vc_mapping')
      .select(`
        vc_id,
        order_num,
        created_at,
        vcs!inner(vc_id, cover_image, width, height)
      `)
      .eq('collection_id', collectionId)
      .eq('accepted', true)
      .order('order_num', { ascending: true })
      .order('created_at', { ascending: true });
      
    if (artworkError) throw artworkError;
    
    // Get existing scene elements for this collection
    const { data: existingElements, error: elementError } = await supabase
      .from('scene_elements')
      .select('*')
      .eq('collection_id', collectionId)
      .eq('type', 'artwork');
      
    if (elementError) throw elementError;
    
    const acceptedVcIds = acceptedArtworks.map(aw => aw.vc_id);
    const existingVcIds = existingElements.map(el => el.vc_id);
    
    // Find artworks to add (in collection but not in scene)
    const toAdd = acceptedArtworks.filter(aw => !existingVcIds.includes(aw.vc_id));
    
    // Find artworks to remove (in scene but not in collection)
    const toRemove = existingElements.filter(el => !acceptedVcIds.includes(el.vc_id));
    
    // Find artworks to update (in both, but metadata might have changed)
    const toUpdate = acceptedArtworks.filter(aw => existingVcIds.includes(aw.vc_id));
    
    console.log(`Found ${toAdd.length} to add, ${toRemove.length} to remove, ${toUpdate.length} to update`);
    
    const results = {
      added: 0,
      removed: 0,
      updated: 0,
      errors: []
    };
    
    // Remove artworks no longer in collection
    if (toRemove.length > 0) {
      const { error: removeError } = await supabase
        .from('scene_elements')
        .delete()
        .in('element_id', toRemove.map(el => el.element_id));
        
      if (removeError) {
        console.error('Error removing artworks:', removeError);
        results.errors.push(`Failed to remove artworks: ${removeError.message}`);
      } else {
        results.removed = toRemove.length;
        console.log(`Removed ${toRemove.length} artworks no longer in collection`);
      }
    }
    
    // Add new artworks with default 3D positioning
    if (toAdd.length > 0) {
      const newElements = toAdd.map((artwork, index) => {
        const row = Math.floor(index / 8);
        const col = index % 8;
        
        return {
          vc_id: artwork.vc_id,
          type: 'artwork',
          source: artwork.vcs.cover_image,
          position_x: -280 + (col * 80),
          position_y: 65,
          position_z: -50 - (row * 100),
          rotation_x: 0,
          rotation_y: 0,
          rotation_z: 0,
          width: artwork.vcs.width,
          height: artwork.vcs.height,
          collection_id: collectionId,
          visible: true,
          opacity: 1.0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      });
      
      const { error: addError } = await supabase
        .from('scene_elements')
        .insert(newElements);
        
      if (addError) {
        console.error('Error adding artworks:', addError);
        results.errors.push(`Failed to add artworks: ${addError.message}`);
      } else {
        results.added = toAdd.length;
        console.log(`Added ${toAdd.length} new artworks`);
      }
    }
    
    // Update metadata for existing artworks (preserve positioning)
    if (toUpdate.length > 0) {
      for (const artwork of toUpdate) {
        const existingElement = existingElements.find(el => el.vc_id === artwork.vc_id);
        if (!existingElement) continue;
        
        // Only update metadata fields, preserve all positioning
        const { error: updateError } = await supabase
          .from('scene_elements')
          .update({
            source: artwork.vcs.cover_image,
            width: artwork.vcs.width,
            height: artwork.vcs.height,
            updated_at: new Date().toISOString()
          })
          .eq('element_id', existingElement.element_id);
          
        if (updateError) {
          console.error(`Error updating artwork ${artwork.vc_id}:`, updateError);
          results.errors.push(`Failed to update artwork ${artwork.vc_id}: ${updateError.message}`);
        } else {
          results.updated++;
        }
      }
      
      console.log(`Updated metadata for ${results.updated} existing artworks`);
    }
    
    return {
      success: results.errors.length === 0,
      message: `Metadata update complete: ${results.added} added, ${results.removed} removed, ${results.updated} updated`,
      details: results
    };
    
  } catch (error) {
    console.error('Error updating collection artwork metadata:', error);
    throw new Error(`Failed to update artwork metadata: ${error.message}`);
  }
};

/**
 * Fetch scene elements with dynamic VCS data (for local testing)
 * This version loads artwork metadata from VCS table instead of scene_elements
 * @param {string} collectionId - The collection ID
 * @param {string} viewMode - '3d' or '2d'
 * @returns {Promise<Object>} - Scene data with walls and unplaced artwork
 */
export const fetchSceneElementsDynamic = async (collectionId, viewMode = '3d') => {
  try {
    console.log(`Fetching scene elements dynamically for collection ${collectionId} in ${viewMode} mode`);
    console.log('collectionId type:', typeof collectionId, 'value:', collectionId);
    
    if (!collectionId) {
      console.error('No collectionId provided to fetchSceneElementsDynamic');
      throw new Error('No collectionId provided');
    }
    
    // Get all scene elements for the collection
    const { data: sceneElements, error: sceneError } = await supabase
      .from('scene_elements')
      .select(`
        *,
        vcs(
          vc_id,
          cover_image,
          width,
          height,
          title,
          description
        )
      `)
      .eq('collection_id', collectionId)
      .eq('visible', true)
      .order('display_order', { ascending: true });
      
    if (sceneError) {
      console.error('Error fetching scene elements:', sceneError);
      throw sceneError;
    }
    
    console.log(`Found ${sceneElements?.length || 0} scene elements`);
    console.log('Sample scene element:', sceneElements?.[0]);
    console.log('All scene elements:', sceneElements);
    
    if (!sceneElements || sceneElements.length === 0) {
      console.log('No scene elements found, returning empty arrays');
      return { walls: [], unplaced: [] };
    }
    
    // Separate walls and artwork (use wall_2d for 2D mode)
    const wallType = viewMode === '2d' ? 'wall_2d' : 'wall';
    const walls = sceneElements.filter(element => element.type === wallType);
    const artworkElements = sceneElements.filter(element => element.type === 'artwork');
    
    console.log(`Found ${walls.length} walls and ${artworkElements.length} artwork elements`);
    console.log('Sample artwork element:', artworkElements?.[0]);
    console.log('All artwork elements:', artworkElements);
    console.log('All walls:', walls);
    
    // Process artwork elements with dynamic VCS data
    const processedArtwork = artworkElements.map(element => {
      // Use VCS data for metadata, scene_elements for positioning
      return {
        element_id: element.element_id,
        vc_id: element.vc_id,
        type: element.type,
        source: element.vcs?.cover_image || element.source, // Dynamic from VCS
        width: element.vcs?.width || element.width, // Dynamic from VCS
        height: element.vcs?.height || element.height, // Dynamic from VCS
        title: element.vcs?.title || element.title, // Dynamic from VCS
        description: element.vcs?.description || element.description, // Dynamic from VCS
        position_x: element.position_x,
        position_y: element.position_y,
        position_z: element.position_z,
        rotation_x: element.rotation_x,
        rotation_y: element.rotation_y,
        rotation_z: element.rotation_z,
        wall_position_x: element.wall_position_x,
        wall_position_y: element.wall_position_y,
        parent_element_id: element.parent_element_id,
        visible: element.visible,
        opacity: element.opacity,
        collection_id: element.collection_id,
        created_at: element.created_at,
        updated_at: element.updated_at
      };
    });
    
    // Group artwork by walls
    const wallsWithArtwork = walls.map(wall => {
      const wallArtwork = processedArtwork.filter(art => 
        art.parent_element_id === wall.element_id
      );
      
      return {
        ...wall,
        artwork: wallArtwork
      };
    });
    
    // Get unplaced artwork (no parent_element_id)
    const unplacedArtwork = processedArtwork.filter(art => 
      !art.parent_element_id
    );
    
    console.log(`Processed ${wallsWithArtwork.length} walls with artwork, ${unplacedArtwork.length} unplaced items`);
    
    return {
      walls: wallsWithArtwork,
      unplaced: unplacedArtwork
    };
    
  } catch (error) {
    console.error('Error fetching scene elements dynamically:', error);
    throw new Error(`Failed to fetch scene elements: ${error.message}`);
  }
};

/**
 * Check which artworks in a collection have audio stories
 * @param {string} collectionId - The collection ID
 * @param {Array} vcIds - Array of vc_ids to check
 * @returns {Promise<Object>} - Object mapping vc_id to boolean
 */
export const checkArtworksWithAudio = async (collectionId, vcIds) => {
  try {
    if (!vcIds || vcIds.length === 0) {
      return {};
    }

    console.log(`🔊 Checking audio stories for ${vcIds.length} artworks in collection ${collectionId}`);

    const { data: audioElements, error } = await supabase
      .from('media_elements')
      .select('vc_id')
      .eq('type', 'Audio')
      .eq('status', 'Public')
      .in('vc_id', vcIds);

    if (error) throw error;

    // Create a set of vc_ids that have audio
    const audioVcIds = new Set(audioElements.map(element => element.vc_id));
    
    // Return object mapping vc_id to boolean
    const audioMap = {};
    vcIds.forEach(vcId => {
      audioMap[vcId] = audioVcIds.has(vcId);
    });

    console.log(`🔊 Found ${audioElements.length} artworks with audio stories`);
    return audioMap;
  } catch (error) {
    console.error('Error checking artworks with audio:', error);
    return {};
  }
};

/**
 * Update wall 3D position in database
 */
export const updateWallPosition = async (wallId, positionData) => {
  try {
    console.log(`🏛️ Updating wall ${wallId} position:`, positionData);
    
    const { data, error } = await supabase
      .from('scene_elements')
      .update({
        wall_center_x: positionData.wall_center_x,
        wall_center_y: positionData.wall_center_y,
        wall_center_z: positionData.wall_center_z,
        wall_rotation_y: positionData.wall_rotation_y || 0,
        updated_at: new Date().toISOString()
      })
      .eq('element_id', wallId)
      .select();
      
    if (error) {
      console.error('Database error updating wall position:', error);
      throw error;
    }
    
    console.log(`✅ Wall ${wallId} position updated successfully`);
    return data[0];
    
  } catch (error) {
    console.error(`❌ Error updating wall ${wallId} position:`, error);
    throw error;
  }
};

/**
 * Convert 2D wall position to 3D world coordinates
 */
export const wallPositionTo3D = (wall, wallX, wallY) => {
  const centerX = wall.wall_center_x || 0;
  const centerY = wall.wall_center_y || (wall.height / 2);
  const centerZ = wall.wall_center_z || 0;
  
  // FIXED: Better coordinate conversion
  // Wall coordinates: (0,0) = top-left, artwork position is top-left of artwork
  // Convert to offset from center - this function is called with artwork position, not wall position
  const offsetX = wallX - (wall.width / 2);     // Horizontal offset from center
  const offsetY = (wall.height / 2) - wallY;    // Vertical offset from center (flip Y)
  
  // Apply wall rotation if any
  const rotationY = (wall.wall_rotation_y || 0) * Math.PI / 180;
  const cos = Math.cos(rotationY);
  const sin = Math.sin(rotationY);
  
  const rotatedX = offsetX * cos;
  const rotatedZ = offsetX * sin;
  
  return {
    x: centerX + rotatedX,
    y: centerY + offsetY,
    z: centerZ + rotatedZ
  };
};

/**
 * Convert 3D world coordinates to 2D wall position
 */
export const threeDToWallPosition = (wall, worldX, worldY, worldZ) => {
  // Get wall center in 3D space
  const centerX = wall.wall_center_x || 0;
  const centerY = wall.wall_center_y || (wall.height / 2);
  const centerZ = wall.wall_center_z || 0;
  
  // Calculate offset from wall center
  const offsetX = worldX - centerX;
  const offsetY = worldY - centerY;
  const offsetZ = worldZ - centerZ;
  
  // Apply inverse wall rotation
  const rotationY = -(wall.wall_rotation_y || 0) * Math.PI / 180;
  const cos = Math.cos(rotationY);
  const sin = Math.sin(rotationY);
  
  // Transform back to wall-local coordinates
  const localX = offsetX * cos - offsetZ * sin;
  
  // Convert to wall surface coordinates (0,0 = top-left of wall)
  const wallX = localX + (wall.width / 2);
  const wallY = -offsetY + (wall.height / 2);  // Flip Y (wall Y=0 is top, 3D Y+ is up)
  
  return {
    x: wallX,
    y: wallY
  };
};

/**
 * Enhanced artwork positioning to sync both 2D and 3D coordinates
 */
export const updateArtworkPositionSynced = async (artworkId, wallId, wallX, wallY) => {
  try {
    console.log(`🎨 Syncing artwork ${artworkId} position: Wall(${wallX}, ${wallY})`);
    
    // Get wall 3D data
    const { data: wall, error: wallError } = await supabase
      .from('scene_elements')
      .select('*')
      .eq('element_id', wallId)
      .eq('type', 'wall_2d')
      .single();
      
    if (wallError) {
      console.error('Error fetching wall data:', wallError);
      throw wallError;
    }
    
    const updateData = {
      parent_element_id: wallId,
      wall_position_x: wallX,
      wall_position_y: wallY,
      updated_at: new Date().toISOString()
    };
    
    // If wall has 3D position, calculate artwork 3D position
    if (wall && wall.wall_center_x !== null) {
      const threeDPos = wallPositionTo3D(wall, wallX, wallY);
      updateData.position_x = threeDPos.x;
      updateData.position_y = threeDPos.y;
      updateData.position_z = threeDPos.z;
      
      console.log(`🔄 Calculated 3D position:`, threeDPos);
    }
    
    const { data, error } = await supabase
      .from('scene_elements')
      .update(updateData)
      .eq('element_id', artworkId)
      .select();
      
    if (error) {
      console.error('Error updating artwork position:', error);
      throw error;
    }
    
    console.log(`✅ Artwork ${artworkId} position synced successfully`);
    return data[0];
    
  } catch (error) {
    console.error(`❌ Error syncing artwork ${artworkId} position:`, error);
    throw error;
  }
};

/**
 * Update 3D position and sync wall position if artwork is on a wall
 */
export const update3DPositionSynced = async (artworkId, worldX, worldY, worldZ) => {
  try {
    console.log(`🌐 Updating artwork ${artworkId} 3D position: (${worldX}, ${worldY}, ${worldZ})`);
    
    // Get artwork data to check if it's on a wall
    const { data: artwork, error: fetchError } = await supabase
      .from('scene_elements')
      .select('parent_element_id')
      .eq('element_id', artworkId)
      .single();
      
    if (fetchError) throw fetchError;
    
    const updateData = {
      position_x: worldX,
      position_y: worldY,
      position_z: worldZ,
      updated_at: new Date().toISOString()
    };
    
    // If artwork is on a wall, also update wall position
    if (artwork.parent_element_id) {
      const { data: wall, error: wallError } = await supabase
        .from('scene_elements')
        .select('*')
        .eq('element_id', artwork.parent_element_id)
        .eq('type', 'wall_2d')
        .single();
        
      if (!wallError && wall && wall.wall_center_x !== null) {
        // Convert 3D position back to wall coordinates
        const wallPos = threeDToWallPosition(wall, worldX, worldY, worldZ);
        
        updateData.wall_position_x = wallPos.x;
        updateData.wall_position_y = wallPos.y;
        
        console.log(`🔄 Calculated wall position:`, wallPos);
      }
    }
    
    const { data, error } = await supabase
      .from('scene_elements')
      .update(updateData)
      .eq('element_id', artworkId)
      .select();
      
    if (error) throw error;
    
    console.log(`✅ Artwork ${artworkId} 3D position updated successfully`);
    return data[0];
    
  } catch (error) {
    console.error(`❌ Error updating artwork ${artworkId} 3D position:`, error);
    throw error;
  }
};

/**
 * Sync all artwork positions on a wall after wall is moved
 */
export const syncArtworkPositionsOnWall = async (wallId) => {
  try {
    console.log(`🔄 Syncing artwork positions for wall ${wallId}`);
    
    // Get wall data
    const { data: wall, error: wallError } = await supabase
      .from('scene_elements')
      .select('*')
      .eq('element_id', wallId)
      .eq('type', 'wall_2d')
      .single();
      
    if (wallError || !wall) {
      console.error('Error fetching wall:', wallError);
      throw wallError || new Error('Wall not found');
    }
    
    // Get all artwork on this wall
    const { data: artworks, error: artworkError } = await supabase
      .from('scene_elements')
      .select('*')
      .eq('parent_element_id', wallId)
      .eq('type', 'artwork');
      
    if (artworkError) {
      console.error('Error fetching artworks:', artworkError);
      throw artworkError;
    }
    
    if (!artworks || artworks.length === 0) {
      console.log(`No artwork found on wall ${wallId}`);
      return { success: true, count: 0 };
    }
    
    console.log(`Found ${artworks.length} artworks to sync on wall ${wallId}`);
    
    // Update 3D positions for all artwork based on new wall position
    const updatePromises = artworks
      .filter(artwork => artwork.wall_position_x !== null && artwork.wall_position_y !== null)
      .map(async (artwork) => {
        const threeDPos = wallPositionTo3D(wall, artwork.wall_position_x, artwork.wall_position_y);
        
        const { error: updateError } = await supabase
          .from('scene_elements')
          .update({
            position_x: threeDPos.x,
            position_y: threeDPos.y,
            position_z: threeDPos.z,
            updated_at: new Date().toISOString()
          })
          .eq('element_id', artwork.element_id);
          
        if (updateError) {
          console.error(`Error updating artwork ${artwork.element_id}:`, updateError);
          throw updateError;
        }
        
        return { artworkId: artwork.element_id, newPosition: threeDPos };
      });
    
    const results = await Promise.all(updatePromises);
    
    console.log(`✅ Synced ${results.length} artwork positions for wall ${wallId}`);
    return { success: true, count: results.length, results };
    
  } catch (error) {
    console.error(`❌ Error syncing artwork positions for wall ${wallId}:`, error);
    throw error;
  }
};

/**
 * Fetch scene elements with 3D wall data included
 */
export const fetchSceneElementsWithWalls = async (collectionId, viewMode = '3d') => {
  try {
    console.log(`Fetching scene elements for collection ${collectionId} (${viewMode} mode)`);
    
    const { data: sceneElements, error } = await supabase
      .from('scene_elements')
      .select(`
        element_id, vc_id, type, source,
        position_x, position_y, position_z,
        rotation_x, rotation_y, rotation_z,
        scale_x, scale_y, scale_z,
        width, height, depth, color, background_color, opacity,
        parent_element_id, wall_position_x, wall_position_y,
        display_order, visible, text_content,
        wall_center_x, wall_center_y, wall_center_z,
        wall_rotation_y, wall_normal_x, wall_normal_y, wall_normal_z
      `)
      .eq('collection_id', collectionId)
      .eq('visible', true);
      
    if (error) {
      console.error('Database error:', error);
      throw error;
    }
    
    if (!sceneElements || sceneElements.length === 0) {
      console.log('No scene elements found');
      return [];
    }
    
    console.log(`Loaded ${sceneElements.length} scene elements`);
    
    // Log wall data for debugging
    const walls = sceneElements.filter(el => el.type === 'wall_2d');
    const wallsWithPosition = walls.filter(w => w.wall_center_x !== null);
    
    console.log(`Found ${walls.length} walls, ${wallsWithPosition.length} with 3D positions`);
    
    // Log detailed wall position data for debugging
    walls.forEach(wall => {
      console.log(`Wall ${wall.element_id} (${wall.source}):`, {
        wall_center_x: wall.wall_center_x,
        wall_center_y: wall.wall_center_y,
        wall_center_z: wall.wall_center_z,
        wall_rotation_y: wall.wall_rotation_y,
        display_order: wall.display_order
      });
    });
    
    return sceneElements;
    
  } catch (error) {
    console.error('Error fetching scene elements:', error);
    throw error;
  }
};

/**
 * Fetch all galleries for dropdown selection
 * @returns {Promise<Array>} - Array of gallery objects with id, name, and address
 */
export const fetchGalleries = async () => {
  try {
    const { data, error } = await supabase
      .from('galleries')
      .select('gallery_id, name, address, type')
      .eq('approved', true)
      .order('name');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching galleries:', error);
    return [];
  }
};

/**
 * Update artwork location (gallery assignment)
 * @param {number} vcId - The artwork ID
 * @param {number|null} galleryId - The gallery ID to assign, or null to remove from gallery
 * @returns {Promise<Object>} - Update result
 */
export const updateArtworkLocation = async (vcId, galleryId) => {
  try {
    const { data, error } = await supabase
      .from('vcs')
      .update({ 
        location: galleryId,
        updated_at: new Date().toISOString()
      })
      .eq('vc_id', vcId)
      .select('vc_id, location');

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error updating artwork location:', error);
    throw error;
  }
};

/**
 * Fetch detailed artwork information
 * @param {string} vcId - The artwork ID
 * @param {string} viewerUuid - The UUID of the viewer (optional)
 * @returns {Promise<Object>} - Detailed artwork data
 */
export const fetchArtworkDetails = async (vcId, viewerUuid = null) => {
  try {
    const { data, error } = await supabase
      .from('vcs')
      .select(`
        vc_id,
        title,
        description,
        cover_image,
        price,
        width,
        height,
        depth,
        status,
        more,
        payment_link,
        audio_story,
        mode,
        created_at,
        updated_at,
        uuid,
        users!inner(
          uuid,
          profile_img,
          handle,
          bio,
          nickname,
          first_name,
          last_name
        )
      `)
      .eq('vc_id', vcId)
      .single();

    if (error) throw error;

    // Get media elements
    const { data: mediaElements } = await supabase
      .from('media_elements')
      .select('source, type, title, text_content')
      .eq('vc_id', vcId)
      .in('type', ['Image', 'Video', 'Audio', 'Writing']);

    // Get attributes (excluding "feeling")
    const { data: attributes } = await supabase
      .from('attribute_vc_mapping')
      .select(`
        artwork_attributes!inner(
          title,
          type
        )
      `)
      .eq('vc_id', vcId)
      .neq('artwork_attributes.type', 'feeling');

    // Get products (prints)
    const { data: products } = await supabase
      .from('products')
      .select('*')
      .eq('vc_id', vcId)
      .eq('status', 'Public')
      .eq('type', 'print');

    // Get user activity counts
    const { count: viewCount } = await supabase
      .from('user_activity')
      .select('*', { count: 'exact', head: true })
      .eq('vc_id', vcId)
      .eq('activity_type', 'view');

    const { count: likeCount } = await supabase
      .from('user_activity')
      .select('*', { count: 'exact', head: true })
      .eq('vc_id', vcId)
      .eq('activity_type', 'liked_artwork');

    const { count: commentCount } = await supabase
      .from('user_activity')
      .select('*', { count: 'exact', head: true })
      .eq('vc_id', vcId)
      .eq('activity_type', 'artwork_comment');

    // Get current gallery location with error handling
    let currentLocation = null;
    try {
      // First get the piece location
      const { data: locationData } = await supabase
        .from('piece_locations')
        .select('beginning, ending, gallery_id')
        .eq('piece_id', vcId)
        .lte('beginning', new Date().toISOString())
        .or('ending.is.null,ending.gte.' + new Date().toISOString())
        .limit(1);
      
      if (locationData && locationData[0]) {
        // Then get the gallery details separately
        const { data: galleryData } = await supabase
          .from('galleries')
          .select('gallery_id, name, address, point_location')
          .eq('gallery_id', locationData[0].gallery_id)
          .single();
        
        if (galleryData) {
          currentLocation = {
            ...locationData[0],
            galleries: galleryData
          };
        }
      }
    } catch (locationError) {
      console.warn('Could not fetch location data:', locationError);
      currentLocation = null;
    }

    // Get more artworks from same artist with error handling
    let moreFromArtist = [];
    try {
      const { data: artistData } = await supabase
        .from('vcs')
        .select(`
          vc_id,
          title,
          cover_image,
          price,
          status
        `)
        .eq('uuid', data.uuid)
        .eq('access_control', 'Public')
        .neq('vc_id', vcId)
        .limit(6);
      
      moreFromArtist = artistData || [];
    } catch (artistError) {
      console.warn('Could not fetch artist data:', artistError);
      moreFromArtist = [];
    }

    // Organize media by type
    const media = mediaElements?.filter(m => ['Image', 'Video'].includes(m.type)) || [];
    const audioClips = mediaElements?.filter(m => m.type === 'Audio') || [];
    const writings = mediaElements?.filter(m => m.type === 'Writing') || [];

    // Build the result object
    const result = {
      ...data,
      isAuction: data.mode === 'Enhanced',
      has_available_prints: products && products.length > 0,
      media,
      audio_clips: audioClips,
      writings,
      attributes: attributes?.map(attr => attr.artwork_attributes) || [],
      view_count: viewCount || 0,
      total_likes: likeCount || 0,
      total_comments: commentCount || 0,
      current_location: currentLocation,
      more_from_artist: moreFromArtist
    };

    return result;
  } catch (error) {
    console.error('Error fetching artwork details:', error);
    throw error;
  }
};

/**
 * Fetch artwork details with extras using the new Supabase Edge Function
 * This matches the FlutterFlow getArtworkDetailsWithExtrasCall functionality
 */
export const fetchArtworkDetailsWithExtras = async (vcId, viewerUuid = null) => {
  try {
    // Use the custom domain for the function
    const functionUrl = 'https://api.vibrantcast.com/functions/v1/get_artwork_details_with_extras';
    
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        vcId: parseInt(vcId),
        viewerUuid: viewerUuid || '00000000-0000-0000-0000-000000000000',
        searchQuery: '%',
        sortBy: 'title',
        sortOrder: 'asc',
        limitVal: 6,
        offsetVal: 0
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching artwork details with extras:', error);
    throw error;
  }
};

// Floor management functions
export const addFloor = async (collectionId, width, depth, height = 0) => {
  try {
    // Base API endpoint - use environment variable if available
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
    const updateEndpoint = `${apiBaseUrl}/update_scene_element`;
    
    const response = await fetch(updateEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        update_mode: "add_floor",
        collection_id: collectionId,
        width: parseFloat(width),
        depth: parseFloat(depth),
        height: parseFloat(height)
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to add floor: ${errorText}`);
    }
    
    const result = await response.json();
    return result;
    
  } catch (error) {
    console.error('Error adding floor:', error);
    
    // Fallback to direct Supabase insert if API fails
    try {
      console.log('Attempting fallback to direct Supabase insert...');
      
      // Insert new floor
      const { data, error: insertError } = await supabase
        .from('scene_elements')
        .insert({
          collection_id: collectionId,
          type: 'floor',
          source: 'Gallery Floor',
          width: parseFloat(width),
          height: parseFloat(depth), // Use height field to store depth
          depth: 1, // Floor has minimal depth
          position_x: '0',
          position_y: height.toString(),
          position_z: '0',
          color: '#8B4513',
          background_color: '#654321',
          text_content: JSON.stringify({
            material: 'wood',
            texture_url: null,
            roughness: 0.7,
            metalness: 0.0,
            tile_size: 24,
            visible: true,
            cast_shadows: true,
            receive_shadows: true
          }),
          visible: true,
          display_order: 0 // Floor is always first
        })
        .select()
        .single();
        
      if (insertError) throw insertError;
      
      return { floor: data };
    } catch (fallbackError) {
      console.error('Fallback insert also failed:', fallbackError);
      throw error; // Throw the original error
    }
  }
};

export const saveFloorData = async (collectionId, floorData) => {
  try {
    // First, check if a floor already exists
    const { data: existingFloor, error: checkError } = await supabase
      .from('scene_elements')
      .select('element_id')
      .eq('collection_id', collectionId)
      .eq('type', 'floor')
      .single();

    if (checkError && checkError.code !== 'PGRST116') {
      throw checkError;
    }

    let result;
    
    if (existingFloor) {
      // Update existing floor
      const { data, error } = await supabase
        .from('scene_elements')
        .update({
          source: 'Gallery Floor',
          position_x: (floorData.position_x || 0).toString(),
          position_y: floorData.height.toString(),
          position_z: (floorData.position_z || 0).toString(),
          width: Math.min(floorData.width, 999.99),
          height: Math.min(floorData.depth, 999.99), // Use depth as height for floor
          depth: 1, // Floor has minimal depth
          color: floorData.color,
          background_color: floorData.grout_color,
          text_content: JSON.stringify({
            material: floorData.material,
            texture_url: floorData.texture_url,
            roughness: floorData.roughness,
            metalness: floorData.metalness,
            tile_size: floorData.tile_size,
            visible: floorData.visible,
            cast_shadows: floorData.cast_shadows,
            receive_shadows: floorData.receive_shadows,
            floorWidth: floorData.width,
            floorDepth: floorData.depth,
            floorPositionX: floorData.position_x || 0,
            floorPositionZ: floorData.position_z || 0
          }),
          visible: floorData.visible,
          display_order: 0, // Floor is always first
          updated_at: new Date().toISOString()
        })
        .eq('element_id', existingFloor.element_id)
        .select();

      if (error) throw error;
      result = data;
    } else {
      // Insert new floor
      const { data, error } = await supabase
        .from('scene_elements')
        .insert({
          collection_id: collectionId,
          type: 'floor',
          source: 'Gallery Floor',
          position_x: (floorData.position_x || 0).toString(),
          position_y: floorData.height.toString(),
          position_z: (floorData.position_z || 0).toString(),
          width: Math.min(floorData.width, 999.99),
          height: Math.min(floorData.depth, 999.99), // Use depth as height for floor
          depth: 1, // Floor has minimal depth
          color: floorData.color,
          background_color: floorData.grout_color,
          text_content: JSON.stringify({
            material: floorData.material,
            texture_url: floorData.texture_url,
            roughness: floorData.roughness,
            metalness: floorData.metalness,
            tile_size: floorData.tile_size,
            visible: floorData.visible,
            cast_shadows: floorData.cast_shadows,
            receive_shadows: floorData.receive_shadows,
            floorWidth: floorData.width,
            floorDepth: floorData.depth,
            floorPositionX: floorData.position_x || 0,
            floorPositionZ: floorData.position_z || 0
          }),
          visible: floorData.visible,
          display_order: 0, // Floor is always first
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select();

      if (error) throw error;
      result = data;
    }

    return result;
  } catch (error) {
    console.error('Error saving floor data:', error);
    throw error;
  }
};

/**
 * Get scene/environment settings for a collection
 * @param {string} collectionId - The collection ID
 * @returns {Promise<Object|null>} - Scene settings or null if not found
 */
export const getSceneSettings = async (collectionId) => {
  try {
    const { data, error } = await supabase
      .from('scene_elements')
      .select('*')
      .eq('collection_id', collectionId)
      .eq('type', 'scene_settings')
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No settings exist, return defaults
        console.log('No scene settings found, using defaults');
        return null;
      }
      throw error;
    }
    
    if (data) {
      // Parse the stored JSON data from text_content
      const settings = JSON.parse(data.text_content || '{}');
      
      console.log('🎨 Loaded scene settings:', settings);
      
      return {
        backgroundColor: settings.backgroundColor || '#000000',
        ambientLightIntensity: settings.ambientLightIntensity ?? 0.6,
        directionalLightIntensity: settings.directionalLightIntensity ?? 1.0,
        presetId: settings.presetId || 'gallery-dark',
        element_id: data.element_id
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting scene settings:', error);
    return null;
  }
};

/**
 * Save scene/environment settings for a collection
 * @param {string} collectionId - The collection ID
 * @param {Object} settings - The scene settings to save
 * @returns {Promise<Object>} - The saved settings
 */
export const saveSceneSettings = async (collectionId, settings) => {
  try {
    console.log('💾 Saving scene settings for collection:', collectionId, settings);
    
    // Check if settings already exist
    const existing = await getSceneSettings(collectionId);
    
    const settingsJson = JSON.stringify({
      backgroundColor: settings.backgroundColor,
      ambientLightIntensity: settings.ambientLightIntensity,
      directionalLightIntensity: settings.directionalLightIntensity,
      presetId: settings.presetId
    });
    
    if (existing && existing.element_id) {
      // Update existing settings
      const { data, error } = await supabase
        .from('scene_elements')
        .update({
          text_content: settingsJson,
          color: settings.backgroundColor,
          updated_at: new Date().toISOString()
        })
        .eq('element_id', existing.element_id)
        .select()
        .single();
      
      if (error) throw error;
      
      console.log('✅ Scene settings updated:', data);
      return settings;
    } else {
      // Create new settings record
      const { data, error } = await supabase
        .from('scene_elements')
        .insert({
          collection_id: collectionId,
          type: 'scene_settings',
          source: 'Scene Settings',
          text_content: settingsJson,
          color: settings.backgroundColor,
          visible: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (error) throw error;
      
      console.log('✅ Scene settings created:', data);
      return settings;
    }
  } catch (error) {
    console.error('Error saving scene settings:', error);
    throw error;
  }
};

export const getFloorData = async (collectionId) => {
  try {
    const { data, error } = await supabase
      .from('scene_elements')
      .select('*')
      .eq('collection_id', collectionId)
      .eq('type', 'floor')
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No floor exists, return null
        return null;
      }
      throw error;
    }
    
    if (data) {
      // Parse the stored JSON data
      const floorConfig = JSON.parse(data.text_content || '{}');
      
      console.log('🔍 Raw floor data from database:', data);
      console.log('🔍 Parsed floor config:', floorConfig);
      
      // Map database fields to component expectations
      // saveFloorData stores: width → width col, depth → height col, thickness → depth col (=1)
      // saveFloorData stores: position → position_x/y/z cols (NOT wall_center_x/y/z)
      // Actual dimensions also stored in text_content as floorWidth/floorDepth to bypass numeric(5,2) limit
      const mappedFloorData = {
        type: 'floor',
        // Prefer text_content dimensions (no DB size limit) over column values
        width: floorConfig.floorWidth || parseFloat(data.width) || 800,
        // Floor Z-depth is stored in `height` column (see saveFloorData: "height: floorData.depth")
        depth: floorConfig.floorDepth || (parseFloat(data.height) > 10 ? parseFloat(data.height) : 1200),
        // Floor thickness stored in `depth` column (always 1)
        height: parseFloat(data.depth) || 4,
        // Position is stored in position_x/y/z columns
        position_x: floorConfig.floorPositionX ?? parseFloat(data.position_x) ?? 0,
        position_y: parseFloat(data.position_y) || 0,
        position_z: floorConfig.floorPositionZ ?? parseFloat(data.position_z) ?? 0,
        // Parse custom properties from text_content
        texture_url: floorConfig.texture_url || null,
        material: floorConfig.material || 'wood',
        color: data.color || '#8B4513',
        roughness: floorConfig.roughness || 0.7,
        metalness: floorConfig.metalness || 0.0,
        tile_size: floorConfig.tile_size || 24,
        grout_color: data.background_color || '#654321',
        visible: data.visible !== false,
        cast_shadows: floorConfig.cast_shadows !== false,
        receive_shadows: floorConfig.receive_shadows !== false
      };
      
      console.log('🔍 Mapped floor data:', mappedFloorData);
      return mappedFloorData;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting floor data:', error);
    return null;
  }
};

/**
 * Search for artist's public/for-sale artwork by name, handle, email, or nickname
 * @param {string} searchQuery - The search term to find artists
 * @returns {Promise<Object>} - Object with artist info and artworks array
 */
export const searchArtistArtwork = async (searchQuery) => {
  try {
    console.log(`🔍 Searching for artist: ${searchQuery}`);
    
    const searchTerm = `%${searchQuery}%`;
    
    // Search for artists by first_name, last_name, email, nickname, or handle
    const { data: artists, error: artistError } = await supabase
      .from('users')
      .select('id, handle, first_name, last_name, nickname, email, uuid')
      .or(`first_name.ilike.${searchTerm},last_name.ilike.${searchTerm},email.ilike.${searchTerm},nickname.ilike.${searchTerm},handle.ilike.${searchTerm}`)
      .limit(10);
    
    if (artistError) throw artistError;
    
    if (!artists || artists.length === 0) {
      console.log('No artists found:', searchQuery);
      return { artist: null, artworks: [], allArtists: [] };
    }
    
    console.log(`Found ${artists.length} artist(s) matching "${searchQuery}"`);
    
    // Get the first matching artist's artwork (or allow selecting from multiple)
    const firstArtist = artists[0];
    
    // Get their public artwork that's for sale (matching by uuid)
    const { data: artworks, error: artworksError } = await supabase
      .from('vcs')
      .select('vc_id, title, cover_image, price, width, height, depth, uuid, created_at')
      .eq('uuid', firstArtist.uuid)
      .eq('access_control', 'Public')
      .not('price', 'is', null)
      .gt('price', 0)
      .order('created_at', { ascending: false });
    
    if (artworksError) throw artworksError;
    
    console.log(`✅ Found ${artworks?.length || 0} public artworks for sale by ${firstArtist.handle || firstArtist.nickname}`);
    
    // Build display name
    const displayName = firstArtist.nickname || 
      `${firstArtist.first_name || ''} ${firstArtist.last_name || ''}`.trim() || 
      firstArtist.handle;
    
    return {
      artist: {
        id: firstArtist.id,
        handle: firstArtist.handle,
        name: displayName,
        first_name: firstArtist.first_name,
        last_name: firstArtist.last_name,
        nickname: firstArtist.nickname
      },
      artworks: artworks || [],
      allArtists: artists.map(a => ({
        id: a.id,
        handle: a.handle,
        name: a.nickname || `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.handle,
        uuid: a.uuid
      }))
    };
    
  } catch (error) {
    console.error('💥 Error searching artist artwork:', error);
    throw new Error(`Failed to search artist artwork: ${error.message}`);
  }
};

/**
 * Get artwork for a specific artist by their uuid
 * @param {string} artistUuid - The artist's uuid
 * @param {Object} artistInfo - Basic artist info for display
 * @returns {Promise<Object>} - Object with artist info and artworks array
 */
export const getArtistArtworkByUuid = async (artistUuid, artistInfo) => {
  try {
    console.log(`🔍 Getting artwork for artist uuid: ${artistUuid}`);
    
    const { data: artworks, error: artworksError } = await supabase
      .from('vcs')
      .select('vc_id, title, cover_image, price, width, height, depth, uuid, created_at')
      .eq('uuid', artistUuid)
      .eq('access_control', 'Public')
      .not('price', 'is', null)
      .gt('price', 0)
      .order('created_at', { ascending: false });
    
    if (artworksError) throw artworksError;
    
    console.log(`✅ Found ${artworks?.length || 0} public artworks for sale`);
    
    return {
      artist: artistInfo,
      artworks: artworks || []
    };
    
  } catch (error) {
    console.error('💥 Error getting artist artwork:', error);
    throw new Error(`Failed to get artist artwork: ${error.message}`);
  }
};

/**
 * Add artwork directly to a collection's scene elements (reverse workflow)
 * Also adds to collection_vc_mapping with accepted=false for curator review
 * @param {number} collectionId - The collection ID to add artwork to
 * @param {Object} artwork - The artwork object from vcs table
 * @returns {Promise<Object>} - The created scene element
 */
export const addArtworkToCollection = async (collectionId, artwork) => {
  try {
    console.log(`🎨 Adding artwork ${artwork.vc_id} to collection ${collectionId}`);
    
    // Check if artwork already exists in scene_elements for this collection
    const { data: existingElement, error: checkElementError } = await supabase
      .from('scene_elements')
      .select('element_id')
      .eq('collection_id', collectionId)
      .eq('vc_id', artwork.vc_id)
      .eq('type', 'artwork')
      .single();
    
    if (existingElement) {
      console.log('Artwork already exists in collection scene_elements');
      return { success: false, message: 'Artwork already exists in collection', existing: true };
    }
    
    // Check if artwork already exists in collection_vc_mapping
    const { data: existingMapping, error: checkMappingError } = await supabase
      .from('collection_vc_mapping')
      .select('mapping_id, accepted')
      .eq('collection_id', collectionId)
      .eq('vc_id', artwork.vc_id)
      .single();
    
    if (existingMapping) {
      console.log('Artwork already exists in collection_vc_mapping, accepted:', existingMapping.accepted);
      // If it exists but wasn't in scene_elements, we still need to add the scene element
    }
    
    // Add to collection_vc_mapping if not already there (with accepted=false for curator review)
    if (!existingMapping) {
      const { data: newMapping, error: mappingError } = await supabase
        .from('collection_vc_mapping')
        .insert({
          collection_id: collectionId,
          vc_id: artwork.vc_id,
          accepted: false, // Curator needs to approve
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (mappingError) {
        console.error('Error adding to collection_vc_mapping:', mappingError);
        // Continue anyway - the scene element is more important for wall planning
      } else {
        console.log('✅ Added to collection_vc_mapping:', newMapping);
      }
    }

    // Also ensure artist is in artist_submission_mapping for the collection's submission
    // This ensures they appear in Communications Hub
    const artistUuid = artwork.uuid || artwork.user_uuid;
    if (artistUuid) {
      // Find the submission_id for this collection
      const { data: submissionData } = await supabase
        .from('submissions_details')
        .select('submission_id')
        .eq('collection_id', collectionId)
        .single();
      
      if (submissionData?.submission_id) {
        // Check if artist already exists in artist_submission_mapping
        const { data: existingArtistMapping } = await supabase
          .from('artist_submission_mapping')
          .select('id')
          .eq('submission_id', submissionData.submission_id)
          .eq('user_id', artistUuid)
          .single();
        
        if (!existingArtistMapping) {
          // Add artist to submission mapping
          const { error: artistMappingError } = await supabase
            .from('artist_submission_mapping')
            .insert({
              user_id: artistUuid,
              submission_id: submissionData.submission_id,
              paid: true, // Mark as paid since curator is manually adding
              created_at: new Date().toISOString()
            });
          
          if (artistMappingError) {
            console.error('Error adding to artist_submission_mapping:', artistMappingError);
          } else {
            console.log(`✅ Added artist ${artistUuid} to artist_submission_mapping for submission ${submissionData.submission_id}`);
          }
        }
      }
    }
    
    // Create the scene element for this artwork
    const { data: newElement, error: insertError } = await supabase
      .from('scene_elements')
      .insert({
        vc_id: artwork.vc_id,
        collection_id: collectionId,
        type: 'artwork',
        source: artwork.cover_image,
        width: artwork.width || 24,
        height: artwork.height || 24,
        depth: artwork.depth || 2,
        position_x: 0,
        position_y: 0,
        position_z: 0,
        rotation_x: 0,
        rotation_y: 0,
        rotation_z: 0,
        visible: true,
        opacity: 1.0,
        parent_element_id: null, // Starts as unplaced
        wall_position_x: null,
        wall_position_y: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();
    
    if (insertError) throw insertError;
    
    console.log(`✅ Added artwork to collection scene_elements:`, newElement);
    
    return { 
      success: true, 
      message: 'Artwork added to collection (pending curator approval)', 
      element: newElement,
      pendingApproval: !existingMapping // true if we created a new mapping
    };
    
  } catch (error) {
    console.error('💥 Error adding artwork to collection:', error);
    throw new Error(`Failed to add artwork to collection: ${error.message}`);
  }
};

/**
 * Remove artwork from a collection's scene elements
 * @param {number} elementId - The scene element ID to remove
 * @returns {Promise<Object>} - Result of the deletion
 */
export const removeArtworkFromCollection = async (elementId) => {
  try {
    console.log(`🗑️ Removing artwork element ${elementId} from collection`);
    
    const { error: deleteError } = await supabase
      .from('scene_elements')
      .delete()
      .eq('element_id', elementId)
      .eq('type', 'artwork');
    
    if (deleteError) throw deleteError;
    
    console.log(`✅ Artwork element removed`);
    
    return { success: true, message: 'Artwork removed from collection' };
    
  } catch (error) {
    console.error('💥 Error removing artwork from collection:', error);
    throw new Error(`Failed to remove artwork from collection: ${error.message}`);
  }
};


/**
 * Get previous collections that used the same gallery as the target collection
 * @param {number} collectionId - The target collection ID
 * @returns {Promise<Array>} - Array of previous collections with wall counts
 */
export const getPreviousGalleryCollections = async (collectionId) => {
  try {
    console.log(`🔍 Finding previous collections using same gallery as collection ${collectionId}`);
    
    // First, get the gallery_id for the target collection
    const { data: targetMapping, error: targetError } = await supabase
      .from('gallery_collection_mapping')
      .select('gallery_id, galleries(gallery_id, name)')
      .eq('collection_id', collectionId)
      .single();
    
    if (targetError || !targetMapping) {
      console.log('No gallery mapping found for target collection');
      return { gallery: null, previousCollections: [] };
    }
    
    const galleryId = targetMapping.gallery_id;
    console.log(`📍 Target collection uses gallery ${galleryId} (${targetMapping.galleries?.name})`);
    
    // Find other collections using the same gallery
    const { data: otherMappings, error: mappingError } = await supabase
      .from('gallery_collection_mapping')
      .select(`
        collection_id,
        collections(collection_id, title, created_at)
      `)
      .eq('gallery_id', galleryId)
      .neq('collection_id', collectionId)
      .order('collection_id', { ascending: false });
    
    if (mappingError) throw mappingError;
    
    if (!otherMappings || otherMappings.length === 0) {
      console.log('No other collections found using the same gallery');
      return { 
        gallery: targetMapping.galleries, 
        previousCollections: [] 
      };
    }
    
    // Get wall counts for each collection
    const collectionIds = otherMappings.map(m => m.collection_id);
    const { data: wallCounts, error: wallError } = await supabase
      .from('scene_elements')
      .select('collection_id')
      .in('collection_id', collectionIds)
      .eq('type', 'wall_2d')
      .eq('visible', true);
    
    if (wallError) throw wallError;
    
    // Count walls per collection
    const wallCountMap = {};
    wallCounts?.forEach(wall => {
      wallCountMap[wall.collection_id] = (wallCountMap[wall.collection_id] || 0) + 1;
    });
    
    // Build result with wall counts
    const previousCollections = otherMappings
      .filter(m => m.collections)
      .map(m => ({
        collection_id: m.collection_id,
        title: m.collections.title,
        created_at: m.collections.created_at,
        wall_count: wallCountMap[m.collection_id] || 0
      }))
      .filter(c => c.wall_count > 0) // Only include collections with walls
      .sort((a, b) => b.wall_count - a.wall_count); // Sort by wall count descending
    
    console.log(`✅ Found ${previousCollections.length} previous collections with walls`);
    
    return {
      gallery: targetMapping.galleries,
      previousCollections
    };
    
  } catch (error) {
    console.error('💥 Error getting previous gallery collections:', error);
    throw new Error(`Failed to get previous gallery collections: ${error.message}`);
  }
};


/**
 * Copy walls from a source collection to a target collection
 * @param {number} sourceCollectionId - The collection to copy walls from
 * @param {number} targetCollectionId - The collection to copy walls to
 * @returns {Promise<Object>} - Result with count of walls copied
 */
export const copyWallsFromCollection = async (sourceCollectionId, targetCollectionId) => {
  try {
    console.log(`📋 Copying walls from collection ${sourceCollectionId} to ${targetCollectionId}`);
    
    // Check if target collection already has walls
    const { data: existingWalls, error: existingError } = await supabase
      .from('scene_elements')
      .select('element_id')
      .eq('collection_id', targetCollectionId)
      .eq('type', 'wall_2d');
    
    if (existingError) throw existingError;
    
    if (existingWalls && existingWalls.length > 0) {
      console.log(`⚠️ Target collection already has ${existingWalls.length} walls`);
      return { 
        success: false, 
        message: `Target collection already has ${existingWalls.length} walls. Please delete them first if you want to copy new walls.`,
        existingWallCount: existingWalls.length
      };
    }
    
    // Get walls from source collection
    const { data: sourceWalls, error: sourceError } = await supabase
      .from('scene_elements')
      .select('*')
      .eq('collection_id', sourceCollectionId)
      .eq('type', 'wall_2d')
      .eq('visible', true);
    
    if (sourceError) throw sourceError;
    
    if (!sourceWalls || sourceWalls.length === 0) {
      return { 
        success: false, 
        message: 'No walls found in source collection' 
      };
    }
    
    console.log(`📦 Found ${sourceWalls.length} walls to copy`);
    
    // Prepare walls for insertion (remove element_id, update collection_id, timestamps)
    const wallsToInsert = sourceWalls.map(wall => {
      const { element_id, created_at, updated_at, ...wallData } = wall;
      return {
        ...wallData,
        collection_id: targetCollectionId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
    });
    
    // Insert walls into target collection
    const { data: insertedWalls, error: insertError } = await supabase
      .from('scene_elements')
      .insert(wallsToInsert)
      .select();
    
    if (insertError) throw insertError;
    
    console.log(`✅ Successfully copied ${insertedWalls.length} walls`);
    
    // Also copy floor if it exists
    const { data: sourceFloor, error: floorError } = await supabase
      .from('scene_elements')
      .select('*')
      .eq('collection_id', sourceCollectionId)
      .eq('type', 'floor')
      .eq('visible', true)
      .single();
    
    if (!floorError && sourceFloor) {
      const { element_id, created_at, updated_at, ...floorData } = sourceFloor;
      const { error: floorInsertError } = await supabase
        .from('scene_elements')
        .insert({
          ...floorData,
          collection_id: targetCollectionId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      
      if (!floorInsertError) {
        console.log('✅ Also copied floor element');
      }
    }
    
    return { 
      success: true, 
      message: `Successfully copied ${insertedWalls.length} walls from collection ${sourceCollectionId}`,
      wallsCopied: insertedWalls.length
    };
    
  } catch (error) {
    console.error('💥 Error copying walls from collection:', error);
    throw new Error(`Failed to copy walls: ${error.message}`);
  }
};


/**
 * Fetch all artworks from collection_vc_mapping for inventory display
 * This includes artworks that haven't been added to scene_elements yet
 * @param {number} collectionId - The collection ID
 * @returns {Promise<Object>} - Object with accepted and pending artworks
 */
export const getCollectionInventoryArtworks = async (collectionId) => {
  try {
    console.log(`📦 Fetching inventory artworks for collection ${collectionId}`);
    
    // Get all artworks from collection_vc_mapping
    const { data: mappings, error: mappingsError } = await supabase
      .from('collection_vc_mapping')
      .select('vc_id, accepted, created_at')
      .eq('collection_id', collectionId);
    
    if (mappingsError) throw mappingsError;
    
    console.log(`📦 Found ${mappings?.length || 0} artworks in collection_vc_mapping`);
    
    if (!mappings || mappings.length === 0) {
      return {
        accepted: [],
        pending: [],
        totalAccepted: 0,
        totalPending: 0,
        totalPlaced: 0
      };
    }
    
    // Get VC details for all mapped artworks
    const vcIds = mappings.map(m => m.vc_id);
    const { data: vcsData, error: vcsError } = await supabase
      .from('vcs')
      .select('vc_id, uuid, title, cover_image, width, height')
      .in('vc_id', vcIds);
    
    if (vcsError) throw vcsError;
    
    // Build a map of vc details
    const vcsMap = (vcsData || []).reduce((acc, vc) => {
      acc[vc.vc_id] = vc;
      return acc;
    }, {});
    
    // Get existing scene_elements for this collection to know which are already placed
    const { data: sceneElements, error: seError } = await supabase
      .from('scene_elements')
      .select('vc_id, element_id, parent_element_id')
      .eq('collection_id', collectionId)
      .eq('type', 'artwork');
    
    if (seError) throw seError;
    
    // Build a map of vc_ids that are already in scene_elements
    const placedVcIds = new Set(sceneElements?.map(se => se.vc_id) || []);
    const placedOnWallVcIds = new Set(
      sceneElements?.filter(se => se.parent_element_id).map(se => se.vc_id) || []
    );
    
    // Get artist names for all artworks
    const artistUuids = [...new Set(Object.values(vcsMap).map(vc => vc.uuid).filter(Boolean))];
    let artistMap = {};
    
    if (artistUuids.length > 0) {
      const { data: artists, error: artistError } = await supabase
        .from('users')
        .select('uuid, nickname, first_name, last_name')
        .in('uuid', artistUuids);
      
      if (!artistError && artists) {
        artistMap = artists.reduce((acc, artist) => {
          acc[artist.uuid] = artist.nickname || 
            `${artist.first_name || ''} ${artist.last_name || ''}`.trim() || 
            'Unknown';
          return acc;
        }, {});
      }
    }
    
    // Transform mappings into inventory items
    const allArtworks = mappings?.map(mapping => {
      const vc = vcsMap[mapping.vc_id];
      if (!vc) return null;
      
      // Use actual dimensions from vcs, converting if needed
      // VCs table stores dimensions - use them directly
      const artWidth = vc.width || 24;  // Default to reasonable size if missing
      const artHeight = vc.height || 24;
      
      return {
        vc_id: vc.vc_id,
        title: vc.title || `Artwork ${vc.vc_id}`,
        source: vc.cover_image,
        width: artWidth,
        height: artHeight,
        accepted: mapping.accepted,
        artist_uuid: vc.uuid,
        artist_name: artistMap[vc.uuid] || 'Unknown',
        isInSceneElements: placedVcIds.has(vc.vc_id),
        isPlacedOnWall: placedOnWallVcIds.has(vc.vc_id),
        created_at: mapping.created_at
      };
    }).filter(Boolean) || [];
    
    // Split into categories
    const acceptedArtworks = allArtworks.filter(a => a.accepted === true);
    const pendingArtworks = allArtworks.filter(a => a.accepted === false);
    
    // Further split by placement status
    const acceptedUnplaced = acceptedArtworks.filter(a => !a.isPlacedOnWall);
    const pendingUnplaced = pendingArtworks.filter(a => !a.isPlacedOnWall);
    
    console.log(`📦 Inventory: ${acceptedUnplaced.length} accepted unplaced, ${pendingUnplaced.length} pending unplaced`);
    
    return {
      accepted: acceptedUnplaced,
      pending: pendingUnplaced,
      totalAccepted: acceptedArtworks.length,
      totalPending: pendingArtworks.length,
      totalPlaced: allArtworks.filter(a => a.isPlacedOnWall).length
    };
    
  } catch (error) {
    console.error('💥 Error fetching collection inventory artworks:', error);
    throw new Error(`Failed to fetch inventory artworks: ${error.message}`);
  }
};

/**
 * Create a scene_element for an artwork and place it on a wall
 * Used when dragging from inventory (collection_vc_mapping) to wall
 * @param {number} collectionId - The collection ID
 * @param {number} vcId - The artwork VC ID
 * @param {number} wallId - The wall element ID
 * @param {number} posX - X position on wall
 * @param {number} posY - Y position on wall
 * @param {Object} artwork - Artwork details (source, width, height, etc.)
 * @returns {Promise<Object>} - New scene_element data
 */
export const createAndPlaceArtwork = async (collectionId, vcId, wallId, posX, posY, artwork) => {
  try {
    console.log(`🎨 Creating scene_element and placing artwork ${vcId} on wall ${wallId}`);
    
    // Check if scene_element already exists for this vc_id in this collection
    const { data: existing, error: existError } = await supabase
      .from('scene_elements')
      .select('element_id')
      .eq('collection_id', collectionId)
      .eq('vc_id', vcId)
      .eq('type', 'artwork')
      .single();
    
    if (existing) {
      // If it exists but wasn't placed, just update its position
      console.log('Scene element exists, updating position:', existing.element_id);
      const { data: updated, error: updateError } = await supabase
        .from('scene_elements')
        .update({
          parent_element_id: wallId,
          wall_position_x: posX,
          wall_position_y: posY,
          updated_at: new Date().toISOString()
        })
        .eq('element_id', existing.element_id)
        .select()
        .single();
      
      if (updateError) throw updateError;
      return { success: true, element: updated, created: false };
    }
    
    // Create new scene_element with wall placement
    const { data: newElement, error: insertError } = await supabase
      .from('scene_elements')
      .insert({
        vc_id: vcId,
        collection_id: collectionId,
        type: 'artwork',
        source: artwork.source,
        width: artwork.width || 24,
        height: artwork.height || 24,
        depth: artwork.depth || 2,
        position_x: 0,
        position_y: 0,
        position_z: 0,
        rotation_x: 0,
        rotation_y: 0,
        rotation_z: 0,
        visible: true,
        opacity: 1.0,
        parent_element_id: wallId,
        wall_position_x: posX,
        wall_position_y: posY,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();
    
    if (insertError) throw insertError;
    
    console.log(`✅ Created and placed artwork scene_element:`, newElement.element_id);
    
    return { success: true, element: newElement, created: true };
    
  } catch (error) {
    console.error('💥 Error creating and placing artwork:', error);
    throw new Error(`Failed to create and place artwork: ${error.message}`);
  }
};

// ============================================================================
// Organization Media Library Functions
// ============================================================================

/**
 * Save a media item to the organization's media library
 * 
 * @param {Object} mediaData - The media data to save
 * @param {string} mediaData.source - Cloudinary URL of the media
 * @param {string} mediaData.organization_id - Organization ID
 * @param {string} [mediaData.type] - Media type: 'image', 'video', 'audio', etc.
 * @param {string} [mediaData.title] - Title of the media
 * @param {string} [mediaData.usage_type] - Usage type: 'email', 'artwork', 'document', 'attachment'
 * @param {string} [mediaData.description] - Description of the media
 * @param {string[]} [mediaData.tags] - Array of tags for searchability
 * @param {boolean} [mediaData.is_downloadable] - Whether the media can be downloaded
 * @returns {Promise<Object>} The created media_element record
 */
export const saveOrganizationMedia = async (mediaData) => {
  try {
    const { data, error } = await supabase
      .from('media_elements')
      .insert({
        source: mediaData.source,
        type: mediaData.type || 'image',
        title: mediaData.title || null,
        organization_id: mediaData.organization_id,
        usage_type: mediaData.usage_type || 'other',
        description: mediaData.description || null,
        tags: mediaData.tags || [],
        is_downloadable: mediaData.is_downloadable ?? true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();
    
    if (error) throw error;
    
    console.log('✅ Saved organization media:', data.element_id);
    return data;
    
  } catch (error) {
    console.error('💥 Error saving organization media:', error);
    throw new Error(`Failed to save organization media: ${error.message}`);
  }
};

/**
 * Fetch media items for an organization
 * 
 * @param {string} organizationId - The organization ID to fetch media for
 * @param {Object} [filters] - Optional filters
 * @param {string} [filters.usageType] - Filter by usage type
 * @param {string} [filters.searchTerm] - Search in description and tags
 * @param {number} [filters.limit] - Max number of results (default 50)
 * @param {number} [filters.offset] - Offset for pagination (default 0)
 * @returns {Promise<Array>} Array of media_element records
 */
export const fetchOrganizationMedia = async (organizationId, filters = {}) => {
  try {
    let query = supabase
      .from('media_elements')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });
    
    // Apply usage type filter
    if (filters.usageType) {
      query = query.eq('usage_type', filters.usageType);
    }
    
    // Apply search filter (searches description)
    if (filters.searchTerm) {
      query = query.ilike('description', `%${filters.searchTerm}%`);
    }
    
    // Apply pagination
    const limit = filters.limit || 50;
    const offset = filters.offset || 0;
    query = query.range(offset, offset + limit - 1);
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    console.log(`✅ Fetched ${data.length} organization media items`);
    return data;
    
  } catch (error) {
    console.error('💥 Error fetching organization media:', error);
    throw new Error(`Failed to fetch organization media: ${error.message}`);
  }
};

/**
 * Update a media item's metadata
 * 
 * @param {number} elementId - The media_element element_id to update
 * @param {Object} updates - The fields to update
 * @returns {Promise<Object>} The updated media_element record
 */
export const updateOrganizationMedia = async (elementId, updates) => {
  try {
    const { data, error } = await supabase
      .from('media_elements')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('element_id', elementId)
      .select()
      .single();
    
    if (error) throw error;
    
    console.log('✅ Updated organization media:', elementId);
    return data;
    
  } catch (error) {
    console.error('💥 Error updating organization media:', error);
    throw new Error(`Failed to update organization media: ${error.message}`);
  }
};

/**
 * Delete a media item from the organization's library
 * Note: This does not delete the file from Cloudinary
 * 
 * @param {number} elementId - The media_element element_id to delete
 * @returns {Promise<boolean>} True if deleted successfully
 */
export const deleteOrganizationMedia = async (elementId) => {
  try {
    const { error } = await supabase
      .from('media_elements')
      .delete()
      .eq('element_id', elementId);
    
    if (error) throw error;
    
    console.log('✅ Deleted organization media:', elementId);
    return true;
    
  } catch (error) {
    console.error('💥 Error deleting organization media:', error);
    throw new Error(`Failed to delete organization media: ${error.message}`);
  }
};
